import type { RootBlockModel } from '@blocksuite/affine-model';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/std';
import {
  type GfxPerformancePhase,
  type GfxPerformanceSample,
  subscribeGfxPerformanceSamples,
} from '@blocksuite/std/gfx';
import type { BlockModel } from '@blocksuite/store';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { strToU8, unzipSync, zipSync } from 'fflate';
import { css, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { literal, unsafeStatic } from 'lit/static-html.js';

import {
  countEstimatedDroppedFrames,
  type DisplayCadence,
  estimateDisplayCadence,
  evaluatePanTestValidity,
  frameDurationsFromTimestamps,
} from './performance-session-metrics.js';

export const AFFINE_CANVAS_PERFORMANCE_OVERLAY =
  'affine-canvas-performance-overlay';

type NumberSeries = {
  durations: number[];
  itemCountMax: number;
  itemCountTotal: number;
};

type ContentSnapshot = {
  blockFlavours: Record<string, number>;
  domBlocks: Array<{
    blockId: string;
    descendantVisibleCssPixelsSum: number;
    descendantBlockCount: number;
    descendants: number;
    flavour: string | null;
    height: number;
    ownBoxVisible: boolean;
    parentBlockId: string | null;
    rendered: boolean;
    styles: Record<string, string>;
    tagName: string;
    visibleDescendantBlockCount: number;
    visibleCssPixels: number;
    width: number;
  }>;
  databaseSurfaces: Array<{
    active: boolean;
    blockId: string | null;
    containment: string;
    descendants: number;
    descendantClassCounts: Record<string, number>;
    descendantTagCounts: Record<string, number>;
    projectedCssPixels: number;
    projectedDevicePixels: number;
    renderedCalendarEvents: number;
    renderedGanttRows: number;
    renderedKanbanCards: number;
    renderedKanbanCells: number;
    renderedKanbanGroups: number;
    renderedTableCells: number;
    renderedTableColumns: number;
    renderedTableRows: number;
    transformedElements: number;
    visibleCssPixels: number;
    visibleDevicePixels: number;
    willChangeElements: number;
  }>;
  domSurfaces: Array<{
    active: boolean;
    blockId: string | null;
    boxShadow: string;
    canvasDescendants: number;
    containment: string;
    contentVisibility: string;
    descendants: number;
    display: string;
    filter: string;
    iframeDescendants: number;
    imageDescendants: number;
    loadedImageDescendants: number;
    logicalCssPixels: number;
    logicalHeight: number;
    logicalWidth: number;
    naturalImagePixels: number;
    opacity: string;
    overflow: string;
    projectedCssPixels: number;
    projectedDevicePixels: number;
    scrollCssPixels: number;
    scrollHeight: number;
    scrollWidth: number;
    svgDescendants: number;
    tagName: string;
    textCharacters: number;
    transform: string;
    visibleCssPixels: number;
    visibleDevicePixels: number;
    willChange: string;
  }>;
  domElements: number;
  surfaceElementTypes: Record<string, number>;
  surfaceElements: number;
  visibleElements: number;
  viewport: {
    height: number;
    width: number;
    zoom: number;
  };
};

type LongAnimationFrameEntry = PerformanceEntry & {
  blockingDuration?: number;
  renderStart?: number;
  scripts?: Array<{
    duration?: number;
    forcedStyleAndLayoutDuration?: number;
    invoker?: string;
    invokerType?: string;
    sourceCharPosition?: number;
    sourceFunctionName?: string;
    sourceURL?: string;
  }>;
  styleAndLayoutStart?: number;
};

type FrameDiagnostic = {
  atMs: number;
  blockProbePhase: BlockProbePhase | null;
  blockProbeSettled: boolean;
  durationMs: number;
  interaction: 'idle' | 'pan' | 'zoom';
  visibleElements: number;
  zoom: number;
};

type BlockProbePhase = 'style-variant';

type DiagnosticScenario = {
  css: string | null;
  descendantSelector?: string;
  forceLayoutAfterViewportTask?: boolean;
  id: string;
  label: string;
  phase: BlockProbePhase | null;
  suspendRemoteSelectionViewportWrites?: boolean;
  suspendViewportBackgroundWrites?: boolean;
  suspendTransformWrites: boolean;
  targetBlockId: string | null;
  targetBlockIds?: string[];
  visualPresentation: 'diagnostic-hidden' | 'retained';
};

type SettleEvidence = {
  durationMs: number;
  reason: 'cadence-stable' | 'timeout';
  stableChecks: number;
  recent: ReturnType<typeof summarize>;
  previous: ReturnType<typeof summarize>;
};

type PanTestMode = 'benchmark' | 'diagnostic-suite';

type CdpCaptureResponse = {
  bundleBase64?: string;
  error?: string;
  status: 'started' | 'stopped';
};

type CdpCaptureBinding = (request: {
  action: 'start' | 'stop';
  captureId: string;
}) => Promise<CdpCaptureResponse>;

type ScriptSeries = {
  count: number;
  durationMs: number;
  forcedStyleAndLayoutDurationMs: number;
  invoker: string | null;
  invokerType: string | null;
  sourceCharPosition: number | null;
  sourceFunctionName: string | null;
  sourceURL: string | null;
};

type ResourceSeries = {
  count: number;
  durationMs: number;
  initiatorType: string;
  transferSize: number;
  url: string;
};

type RawGfxSample = {
  atMs: number;
  detail?: GfxPerformanceSample['detail'];
  durationMs: number;
  itemCount?: number;
  phase: GfxPerformancePhase;
};

type DomMutationSeries = {
  addedNodes: number;
  attributeName: string | null;
  attributeChanges: number;
  count: number;
  removedNodes: number;
  tagName: string;
  type: MutationRecordType;
};

type CanvasElementSeries = {
  elementId: string;
  elementType: string;
  renderDurations: number[];
  visibilityDurations: number[];
  visibilityChecks: number;
  visibleChecks: number;
};

type DetailedGfxSeries = {
  detail: GfxPerformanceSample['detail'];
  durations: number[];
  itemCountMax: number;
  itemCountTotal: number;
  phase: GfxPerformancePhase;
};

type BlockRuntimeSeries = {
  attributeMutations: number;
  blockId: string;
  childListMutations: number;
  descendantMutations: number;
  flavour: string | null;
  litUpdateDurations: number[];
  mutationTargets: Record<string, number>;
  parentBlockId: string | null;
  selfMutations: number;
  stylePropertiesChanged: Record<string, number>;
  stylePropertiesObserved: Record<string, number>;
  styleMutations: number;
  tagName: string | null;
  transformDurations: number[];
  updateReasons: Record<string, number>;
};

const MAX_SAMPLES = 36_000;
const MAX_RAW_GFX_SAMPLES = 100_000;
const PAN_TEST_DURATION_MS = 3_000;
const PAN_TEST_HORIZONTAL_AMPLITUDE_PX = 240;
const PAN_TEST_VERTICAL_AMPLITUDE_PX = 120;
const PAN_TEST_MIN_COVERAGE = 0.7;
const SETTLE_MIN_MS = 3_000;
const SETTLE_MAX_MS = 15_000;
const SETTLE_WINDOW_MS = 1_000;
const SETTLE_CHECK_INTERVAL_MS = 500;
const SETTLE_REQUIRED_CHECKS = 3;
const BLOCK_PROBE_ATTRIBUTE = 'data-streamlain-performance-block-probe';
const GFX_DOM_SURFACE_SELECTOR = [
  'affine-edgeless-database',
  'affine-edgeless-image',
  'affine-edgeless-note',
  'affine-edgeless-text',
  'affine-frame',
].join(',');
const samplePositions = new WeakMap<number[], number>();

function addSample(samples: number[], value: number) {
  const position = samplePositions.get(samples) ?? 0;
  if (samples.length < MAX_SAMPLES) samples.push(value);
  else samples[position % MAX_SAMPLES] = value;
  samplePositions.set(samples, position + 1);
}

function rounded(value: number, digits = 2) {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function cssDeclaration(value: string | null) {
  const declaration = new Map<string, string>();
  if (!value) return declaration;
  value.split(';').forEach(entry => {
    const separator = entry.indexOf(':');
    if (separator < 0) return;
    const property = entry.slice(0, separator).trim();
    if (!property) return;
    declaration.set(property, entry.slice(separator + 1).trim());
  });
  return declaration;
}

function changedCssProperties(before: string | null, after: string | null) {
  const previous = cssDeclaration(before);
  const next = cssDeclaration(after);
  return new Set(
    [...previous.keys(), ...next.keys()].filter(
      property => previous.get(property) !== next.get(property)
    )
  );
}

function collectUserTimingEntries(sessionStartedAt: number) {
  return performance
    .getEntries()
    .filter(
      entry =>
        (entry.entryType === 'mark' || entry.entryType === 'measure') &&
        entry.startTime >= sessionStartedAt
    )
    .map(entry => ({
      atMs: rounded(entry.startTime - sessionStartedAt, 3),
      durationMs: rounded(entry.duration, 3),
      entryType: entry.entryType,
      name: entry.name,
    }))
    .sort((left, right) => left.atMs - right.atMs);
}

function summarize(samples: number[]) {
  if (samples.length === 0) {
    return {
      count: 0,
      maxMs: 0,
      meanMs: 0,
      p50Ms: 0,
      p95Ms: 0,
      p99Ms: 0,
      totalMs: 0,
    };
  }
  const sorted = [...samples].sort((a, b) => a - b);
  const percentile = (value: number) =>
    sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * value))];
  return {
    count: samples.length,
    maxMs: rounded(sorted.at(-1) ?? 0),
    meanMs: rounded(
      samples.reduce((sum, value) => sum + value, 0) / samples.length
    ),
    p50Ms: rounded(percentile(0.5)),
    p95Ms: rounded(percentile(0.95)),
    p99Ms: rounded(percentile(0.99)),
    totalMs: rounded(samples.reduce((sum, value) => sum + value, 0)),
  };
}

function summarizeValues(samples: number[]) {
  if (samples.length === 0) return { count: 0, max: 0, mean: 0, total: 0 };
  const total = samples.reduce((sum, value) => sum + value, 0);
  return {
    count: samples.length,
    max: rounded(Math.max(...samples), 4),
    mean: rounded(total / samples.length, 4),
    total: rounded(total, 4),
  };
}

function safeResourceUrl(value: string) {
  try {
    const url = new URL(value, window.location.href);
    return `${url.origin === window.location.origin ? '' : url.origin}${url.pathname}`;
  } catch {
    return value.slice(0, 240);
  }
}

function addBucketSample(
  buckets: Map<string, number[]>,
  bucket: string,
  value: number
) {
  const samples = buckets.get(bucket) ?? [];
  addSample(samples, value);
  buckets.set(bucket, samples);
}

function visibleElementsBucket(count: number) {
  if (count < 10) return '0-9';
  if (count < 25) return '10-24';
  if (count < 50) return '25-49';
  if (count < 100) return '50-99';
  return '100+';
}

function zoomBucket(zoom: number) {
  if (zoom < 0.25) return '<0.25';
  if (zoom < 0.5) return '0.25-0.49';
  if (zoom < 1) return '0.5-0.99';
  if (zoom < 2) return '1-1.99';
  return '2+';
}

function summarizeFrameBuckets(
  buckets: Map<string, number[]>,
  cadence: DisplayCadence | null
) {
  return Object.fromEntries(
    [...buckets.entries()].map(([bucket, durations]) => [
      bucket,
      {
        ...summarize(durations),
        estimatedDroppedFrames: cadence
          ? countEstimatedDroppedFrames(durations, cadence.expectedFrameMs)
          : 0,
        overBudgetFrames: cadence
          ? durations.filter(
              duration => duration >= cadence.expectedFrameMs * 1.5
            ).length
          : 0,
      },
    ])
  );
}

function viewportCoherenceInventory(samples: RawGfxSample[]) {
  type DomTransform = {
    atMs: number;
    blockId: string;
    cssTransform: string | null;
    modelXYWH: string | null;
    tagName: string;
  };
  type EdgeTransform = {
    atMs: number;
    cssTransform: string | null;
    expectedCssTransform: string | null;
    transformMatchesExpected: boolean;
  };
  const states = new Map<
    string,
    {
      canvasAtMs: number[];
      domTransforms: DomTransform[];
      edgeTransforms: EdgeTransform[];
    }
  >();

  samples.forEach(sample => {
    if (
      sample.phase !== 'canvas-render' &&
      sample.phase !== 'viewport-dom-transform' &&
      sample.phase !== 'edge-viewport-transform'
    )
      return;
    const viewportState = sample.detail?.viewportState;
    if (typeof viewportState !== 'string') return;
    const state = states.get(viewportState) ?? {
      canvasAtMs: [],
      domTransforms: [],
      edgeTransforms: [],
    };
    if (sample.phase === 'canvas-render') {
      state.canvasAtMs.push(sample.atMs);
    } else if (sample.phase === 'viewport-dom-transform') {
      state.domTransforms.push({
        atMs: sample.atMs,
        blockId: String(sample.detail?.blockId ?? 'unknown'),
        cssTransform:
          typeof sample.detail?.cssTransform === 'string'
            ? sample.detail.cssTransform
            : null,
        modelXYWH:
          typeof sample.detail?.modelXYWH === 'string'
            ? sample.detail.modelXYWH
            : null,
        tagName: String(sample.detail?.tagName ?? 'unknown'),
      });
    } else {
      state.edgeTransforms.push({
        atMs: sample.atMs,
        cssTransform:
          typeof sample.detail?.cssTransform === 'string'
            ? sample.detail.cssTransform
            : null,
        expectedCssTransform:
          typeof sample.detail?.expectedCssTransform === 'string'
            ? sample.detail.expectedCssTransform
            : null,
        transformMatchesExpected:
          sample.detail?.transformMatchesExpected === true,
      });
    }
    states.set(viewportState, state);
  });

  const canvasStates = new Set(
    [...states.entries()]
      .filter(([, state]) => state.canvasAtMs.length > 0)
      .map(([viewportState]) => viewportState)
  );
  const elementStates = new Map<
    string,
    {
      blockId: string;
      tagName: string;
      states: Set<string>;
      skewsMs: number[];
    }
  >();
  const stateTimeline = [...states.entries()].map(([viewportState, state]) => {
    const canvasAtMs = state.canvasAtMs[0] ?? null;
    const domTimes = state.domTransforms.map(transform => transform.atMs);
    const edgeTimes = state.edgeTransforms.map(transform => transform.atMs);
    const uniqueDomElements = new Set(
      state.domTransforms.map(transform => transform.blockId)
    );
    state.domTransforms.forEach(transform => {
      const element = elementStates.get(transform.blockId) ?? {
        blockId: transform.blockId,
        tagName: transform.tagName,
        states: new Set<string>(),
        skewsMs: [],
      };
      element.states.add(viewportState);
      if (canvasAtMs !== null)
        element.skewsMs.push(transform.atMs - canvasAtMs);
      elementStates.set(transform.blockId, element);
    });

    return {
      viewportState,
      canvasRenderCount: state.canvasAtMs.length,
      canvasAtMs,
      domElementCount: uniqueDomElements.size,
      domTransformCount: state.domTransforms.length,
      firstDomAtMs: domTimes.length ? Math.min(...domTimes) : null,
      lastDomAtMs: domTimes.length ? Math.max(...domTimes) : null,
      canvasToLastDomSkewMs:
        canvasAtMs !== null && domTimes.length
          ? rounded(Math.max(...domTimes) - canvasAtMs, 3)
          : null,
      edgeTransformCount: state.edgeTransforms.length,
      edgeAtMs: edgeTimes[0] ?? null,
      canvasToEdgeSkewMs:
        canvasAtMs !== null && edgeTimes.length
          ? rounded(edgeTimes[0] - canvasAtMs, 3)
          : null,
      edgeTransformMatchesExpected: state.edgeTransforms.every(
        transform => transform.transformMatchesExpected
      ),
    };
  });
  const elements = [...elementStates.values()]
    .map(element => {
      const missingCanvasStates = [...canvasStates].filter(
        viewportState => !element.states.has(viewportState)
      ).length;
      return {
        blockId: element.blockId,
        tagName: element.tagName,
        capturedStates: element.states.size,
        missingCanvasStates,
        skewMs: summarize(element.skewsMs),
        maxAbsoluteSkewMs: rounded(
          Math.max(0, ...element.skewsMs.map(value => Math.abs(value))),
          3
        ),
      };
    })
    .sort(
      (left, right) =>
        right.missingCanvasStates - left.missingCanvasStates ||
        right.maxAbsoluteSkewMs - left.maxAbsoluteSkewMs
    );

  return {
    canvasViewportStates: canvasStates.size,
    domViewportStates: [...states.values()].filter(
      state => state.domTransforms.length > 0
    ).length,
    statesWithoutCanvasRender: [...states.values()].filter(
      state => state.canvasAtMs.length === 0 && state.domTransforms.length > 0
    ).length,
    statesWithoutDomTransform: [...states.values()].filter(
      state => state.canvasAtMs.length > 0 && state.domTransforms.length === 0
    ).length,
    statesWithoutEdgeTransform: [...states.values()].filter(
      state => state.canvasAtMs.length > 0 && state.edgeTransforms.length === 0
    ).length,
    edgeTransformMismatches: [...states.values()].reduce(
      (count, state) =>
        count +
        state.edgeTransforms.filter(
          transform => !transform.transformMatchesExpected
        ).length,
      0
    ),
    elements,
    stateTimeline,
  };
}

function summarizeDetailedGfx(groups: Map<string, DetailedGfxSeries>) {
  return [...groups.values()]
    .map(group => ({
      detail: group.detail,
      phase: group.phase,
      ...summarize(group.durations),
      meanItems:
        group.durations.length > 0
          ? rounded(group.itemCountTotal / group.durations.length, 1)
          : 0,
      maxItems: group.itemCountMax,
    }))
    .sort((left, right) => right.totalMs - left.totalMs);
}

function canvasElementInventory(elements: Map<string, CanvasElementSeries>) {
  return [...elements.values()]
    .map(element => ({
      elementId: element.elementId,
      elementType: element.elementType,
      render: summarize(element.renderDurations),
      visibility: summarize(element.visibilityDurations),
      visibilityChecks: element.visibilityChecks,
      visibleChecks: element.visibleChecks,
    }))
    .sort((left, right) => right.render.totalMs - left.render.totalMs);
}

function blockRuntimeInventory(blocks: Map<string, BlockRuntimeSeries>) {
  return [...blocks.values()]
    .map(block => {
      const litUpdate = summarize(block.litUpdateDurations);
      const viewportTransform = summarize(block.transformDurations);
      return {
        ...block,
        litUpdate,
        totalMeasuredJsMs: rounded(
          litUpdate.totalMs + viewportTransform.totalMs,
          3
        ),
        viewportTransform,
        litUpdateDurations: undefined,
        transformDurations: undefined,
      };
    })
    .sort((left, right) => right.totalMeasuredJsMs - left.totalMeasuredJsMs);
}

function isEditingTarget(event: KeyboardEvent) {
  if (event.isComposing) return true;
  return event.composedPath().some(target => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      target.isContentEditable ||
      target.matches('input, textarea, select, [contenteditable="true"]')
    );
  });
}

export class AffineCanvasPerformanceOverlay extends WidgetComponent<RootBlockModel> {
  static override styles = css`
    :host {
      position: absolute;
      inset: 0;
      z-index: var(--streamlain-z-index-popover);
      pointer-events: none !important;
    }

    .panel {
      position: absolute;
      top: 16px;
      left: 16px;
      box-sizing: border-box;
      width: 288px;
      padding: 12px;
      overflow: hidden;
      border: 0.5px solid
        var(
          --streamlain-v2-layer-insideBorder-border,
          rgba(127, 127, 127, 0.24)
        );
      border-radius: 12px;
      color: var(--streamlain-v2-text-primary, #f4f4f4);
      background: var(--streamlain-v2-layer-background-overlayPanel, #171717);
      box-shadow: var(
        --streamlain-overlay-panel-shadow,
        0 12px 32px rgba(0, 0, 0, 0.18),
        0 2px 6px rgba(0, 0, 0, 0.08)
      );
      pointer-events: auto;
      font:
        12px/1.4 Inter,
        system-ui,
        sans-serif;
      animation: panel-enter 140ms ease-out;
    }

    .header,
    .title-meta,
    .actions,
    .metric {
      display: flex;
      align-items: center;
    }

    .header {
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .title-copy {
      display: grid;
      gap: 3px;
      min-width: 0;
    }

    .title {
      overflow: hidden;
      font-size: 13px;
      font-weight: 650;
      letter-spacing: -0.01em;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .label,
    .hint {
      color: var(--streamlain-v2-text-secondary, #aaa);
    }

    .title-meta {
      gap: 6px;
    }

    .status {
      display: inline-flex;
      gap: 5px;
      align-items: center;
      color: var(--streamlain-v2-text-secondary, #aaa);
      font-size: 10px;
    }

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--streamlain-success-color, #45b97c);
    }

    .status.recording .status-dot {
      background: var(--streamlain-error-color, #e45656);
      animation: status-pulse 1.4s ease-in-out infinite;
    }

    .meta-divider {
      color: var(--streamlain-v2-text-placeholder, #777);
    }

    .sample-state {
      color: var(--streamlain-v2-text-secondary, #aaa);
      font-size: 10px;
      font-variant-numeric: tabular-nums;
    }

    .metrics {
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 10px;
      overflow: hidden;
      border: 0.5px solid
        var(--streamlain-v2-layer-insideBorder-border, rgba(127, 127, 127, 0.2));
      border-radius: 9px;
      background: var(--streamlain-v2-layer-background-primary, transparent);
    }

    .metric {
      box-sizing: border-box;
      min-width: 0;
      min-height: 50px;
      padding: 8px 10px;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      background: transparent;
    }

    .metric:not(.fps):last-child {
      border-left: 0.5px solid
        var(--streamlain-v2-layer-insideBorder-border, rgba(127, 127, 127, 0.2));
    }

    .metric.fps {
      min-height: 54px;
      padding: 9px 10px;
      grid-column: 1 / -1;
      flex-direction: row;
      align-items: center;
      border-bottom: 0.5px solid
        var(--streamlain-v2-layer-insideBorder-border, rgba(127, 127, 127, 0.2));
      background: var(
        --streamlain-v2-layer-background-hoverOverlay,
        transparent
      );
    }

    .metric-copy {
      display: grid;
      gap: 1px;
    }

    .value {
      color: var(--streamlain-v2-text-primary, #f4f4f4);
      font-size: 16px;
      font-weight: 650;
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.02em;
      white-space: nowrap;
    }

    .fps .value {
      font-size: 20px;
      line-height: 1;
    }

    .fps .unit {
      margin-left: 3px;
      color: var(--streamlain-v2-text-secondary, #aaa);
      font-size: 9px;
      font-weight: 550;
      letter-spacing: 0.04em;
    }

    .label {
      font-size: 10px;
      font-weight: 500;
    }

    .metric-detail {
      color: var(--streamlain-v2-text-secondary, #aaa);
      font-size: 9px;
    }

    .capture-path {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 6px;
      margin-top: 6px;
    }

    .capture-error {
      margin-top: 6px;
      color: var(--streamlain-error-color, #e45656);
      font-size: 10px;
    }

    .capture-path input {
      min-width: 0;
      padding: 0 8px;
      border: 0.5px solid
        var(--streamlain-v2-layer-insideBorder-border, rgba(127, 127, 127, 0.2));
      border-radius: 7px;
      color: var(--streamlain-v2-text-secondary, #aaa);
      background: var(--streamlain-v2-layer-background-secondary, #242424);
      font: inherit;
      font-size: 9px;
    }

    .actions {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 6px;
    }

    .cdp-option {
      display: flex;
      gap: 7px;
      align-items: center;
      margin-bottom: 8px;
      color: var(--streamlain-v2-text-secondary, #aaa);
      cursor: pointer;
      font-size: 10px;
    }

    .cdp-option input {
      width: 14px;
      height: 14px;
      margin: 0;
      accent-color: var(--streamlain-v2-button-primary, #5090d3);
    }

    .cdp-option:has(input:disabled) {
      color: var(--streamlain-v2-text-disable, #777);
      cursor: default;
    }

    button {
      min-height: 32px;
      padding: 0 10px;
      border: 0.5px solid transparent;
      border-radius: 8px;
      color: var(--streamlain-v2-text-primary, inherit);
      background: var(
        --streamlain-v2-button-secondary,
        rgba(127, 127, 127, 0.14)
      );
      cursor: pointer;
      font: inherit;
      font-size: 11px;
      font-weight: 550;
      transition:
        transform 120ms ease,
        background 120ms ease,
        border-color 120ms ease,
        opacity 120ms ease;
    }

    button:not(:disabled):hover {
      border-color: var(
        --streamlain-v2-layer-insideBorder-border,
        rgba(127, 127, 127, 0.28)
      );
      background: var(
        --streamlain-v2-button-buttonOverHover,
        rgba(127, 127, 127, 0.2)
      );
    }

    button:not(:disabled):active {
      transform: scale(0.975);
    }

    button.primary {
      color: var(--streamlain-v2-button-pureWhiteText, white);
      background: var(--streamlain-v2-button-primary, #5090d3);
    }

    button.primary:not(:disabled):hover {
      border-color: transparent;
      background: color-mix(
        in srgb,
        var(--streamlain-v2-button-primary, #5090d3) 88%,
        white
      );
    }

    button.danger {
      color: white;
      border-color: transparent;
      background: var(--streamlain-error-color, #d94a4a);
    }

    button.danger:not(:disabled):hover {
      border-color: transparent;
      background: color-mix(
        in srgb,
        var(--streamlain-error-color, #d94a4a) 88%,
        black
      );
    }

    button.icon {
      display: grid;
      width: 28px;
      min-height: 28px;
      padding: 0;
      place-items: center;
      border-radius: 7px;
      color: var(--streamlain-v2-icon-primary, #aaa);
      background: transparent;
      font-size: 16px;
      font-weight: 400;
    }

    button:disabled {
      color: var(--streamlain-v2-text-disable, #777);
      background: color-mix(
        in srgb,
        var(--streamlain-v2-button-disable, rgba(127, 127, 127, 0.12)) 70%,
        transparent
      );
      opacity: 0.68;
      cursor: default;
    }

    .hint {
      display: flex;
      gap: 7px;
      align-items: center;
      margin-top: 10px;
      padding-top: 9px;
      border-top: 0.5px solid
        var(--streamlain-v2-layer-insideBorder-border, rgba(127, 127, 127, 0.2));
      font-size: 9px;
    }

    kbd {
      padding: 1px 4px;
      border: 0.5px solid
        var(
          --streamlain-v2-layer-insideBorder-border,
          rgba(127, 127, 127, 0.28)
        );
      border-radius: 5px;
      color: var(--streamlain-v2-text-primary, #f4f4f4);
      background: var(--streamlain-v2-layer-background-secondary, #242424);
      box-shadow: 0 1px 0
        var(--streamlain-v2-layer-insideBorder-border, rgba(127, 127, 127, 0.2));
      font: inherit;
      white-space: nowrap;
    }

    @keyframes panel-enter {
      from {
        opacity: 0;
        transform: translateY(-2px);
      }
    }

    @keyframes status-pulse {
      50% {
        opacity: 0.55;
        transform: scale(0.82);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .panel,
      .status.recording .status-dot {
        animation: none;
      }

      button {
        transition: none;
      }
    }
  `;

  private readonly _performanceObservers: PerformanceObserver[] = [];

  private _domMutationObserver: MutationObserver | null = null;

  private readonly _domMutations = new Map<string, DomMutationSeries>();

  private _domMutationObserverDurations: number[] = [];

  private _frameRequest = 0;

  private _frameSamples: number[] = [];

  private _frameTimeline: FrameDiagnostic[] = [];

  private _fpsFrameRequest = 0;

  private _fpsFrameTimes: number[] = [];

  private _lastFpsUpdateAt = 0;

  private _interactionDurations: number[] = [];

  private _interactionInputDelays: number[] = [];

  private _interactionPresentationDelays: number[] = [];

  private _interactionProcessingDurations: number[] = [];

  private _interactionEvents: Record<string, number> = {};

  private _lastFrameAt = 0;

  private _lastUiUpdateAt = 0;

  private _longTaskDurations: number[] = [];

  private _longAnimationBlockingDurations: number[] = [];

  private _longAnimationDurations: number[] = [];

  private _longAnimationForcedStyleDurations: number[] = [];

  private _longAnimationRenderDurations: number[] = [];

  private _longAnimationScriptDurations: number[] = [];

  private _rawLongAnimationFrames: Array<Record<string, unknown>> = [];

  private readonly _longAnimationScripts = new Map<string, ScriptSeries>();

  private _layoutShiftScores: number[] = [];

  private _unexpectedLayoutShiftScores: number[] = [];

  private _gcDurations: number[] = [];

  private readonly _resources = new Map<string, ResourceSeries>();

  private _rawResources: Array<Record<string, unknown>> = [];

  private _rawGfxSamples: RawGfxSample[] = [];

  private _rawGfxSampleCount = 0;

  private readonly _canvasElementSamples = new Map<
    string,
    CanvasElementSeries
  >();

  private readonly _blockRuntimeSamples = new Map<string, BlockRuntimeSeries>();

  private readonly _detailedGfxSamples = new Map<string, DetailedGfxSeries>();

  private _gfxCollectorDurations: number[] = [];

  private _panDriverDurations: number[] = [];

  private readonly _frameInteractionBuckets = new Map<string, number[]>();

  private readonly _frameVisibleBuckets = new Map<string, number[]>();

  private readonly _frameZoomBuckets = new Map<string, number[]>();

  private _blockProbeDomTargets: HTMLElement[] = [];

  private _blockProbeTopLevelDomTargets: HTMLElement[] = [];

  private _blockProbeTopLevelDatabaseTargets: HTMLElement[] = [];

  private _blockProbeTargetInfo: Record<string, unknown> | null = null;

  private _blockProbePhase: BlockProbePhase | null = null;

  private _blockProbeStyle: HTMLStyleElement | null = null;

  private _activeDiagnosticScenario: DiagnosticScenario | null = null;

  private _diagnosticScenarios: DiagnosticScenario[] = [];

  private _blockProbeCandidates: Array<{
    element: HTMLElement;
    info: Record<string, unknown>;
  }> = [];

  private _blockProbeRootTarget: HTMLElement | null = null;

  private _diagnosticSuiteEntries: Record<string, Uint8Array> = {};

  private _diagnosticSuiteReports: Array<Record<string, unknown>> = [];

  private _diagnosticSuiteScenarioIndex = 0;

  private _settleAnimationFrame = 0;

  private _settleEvidence: SettleEvidence | null = null;

  private _latestVisibleItems = 0;

  private _recordingCadence: DisplayCadence | null = null;

  private _recordingAbort: AbortController | null = null;

  private _panTestAnimationFrame = 0;

  private _panTestAnimationStartedAt = 0;

  private _panTestDistanceCssPx = 0;

  private _panTestInitialViewport: {
    centerX: number;
    centerY: number;
    zoom: number;
  } | null = null;

  private _panTestLastCenter: { x: number; y: number } | null = null;

  private _panTestMovementUpdates = 0;

  private _panTestStopTimeout = 0;

  private _panTestTargetEndAt = 0;

  private _panTestMode: PanTestMode = 'benchmark';

  private readonly _renderSamples = new Map<
    GfxPerformancePhase,
    NumberSeries
  >();

  private _sessionStartedAt = 0;

  private _sessionStartedAtEpochMs = 0;

  private _startContent: ContentSnapshot | null = null;

  private _startHeapMb: number | null = null;

  private _reportObject: Record<string, unknown> | null = null;

  private _cdpCaptureEntries: Record<string, Uint8Array> = {};

  private _cdpCaptureId: string | null = null;

  private _panTestCapturesCdp = false;

  private _unsubscribePerformance: (() => void) | null = null;

  @state()
  private accessor _copied = false;

  @state()
  private accessor _copyFailed = false;

  @state()
  private accessor _cdpCaptureError = '';

  @state()
  private accessor _cdpCaptureEnabled = true;

  @state()
  private accessor _storedCapturePath = '';

  @state()
  private accessor _currentFps = 0;

  @state()
  private accessor _countdownMs = 0;

  @state()
  private accessor _elapsedMs = 0;

  @state()
  private accessor _report = '';

  @state()
  private accessor _reportValid: boolean | null = null;

  @state()
  private accessor _recording = false;

  @state()
  private accessor _armed = false;

  @state()
  private accessor _suiteProgress = '';

  @state()
  private accessor _visible = false;

  override connectedCallback() {
    super.connectedCallback();
    this.bindHotKey(
      {
        'Shift-Alt-f': ctx => {
          const event = ctx.get('keyboardState').raw;
          if (
            !this._isEdgeless() ||
            this._isGfxEditing() ||
            isEditingTarget(event)
          )
            return false;
          event.preventDefault();
          event.stopPropagation();
          this._setVisible(!this._visible);
          return true;
        },
      },
      { global: true }
    );
  }

  override disconnectedCallback() {
    this._cancelPanTestTimers();
    this._restoreBlockProbe();
    this._restorePanTestViewport();
    this._stopFpsCounter();
    this._stopCollectors();
    super.disconnectedCallback();
  }

  private readonly _collectFps = (timestamp: number) => {
    if (!this._visible) return;
    this._fpsFrameTimes.push(timestamp);
    const windowStart = timestamp - 1000;
    while (
      this._fpsFrameTimes.length > 0 &&
      this._fpsFrameTimes[0] < windowStart
    )
      this._fpsFrameTimes.shift();

    if (timestamp - this._lastFpsUpdateAt >= 500) {
      const first = this._fpsFrameTimes[0];
      const elapsed = first === undefined ? 0 : timestamp - first;
      this._currentFps =
        elapsed > 0
          ? Math.round(((this._fpsFrameTimes.length - 1) * 1000) / elapsed)
          : 0;
      this._lastFpsUpdateAt = timestamp;
    }
    this._fpsFrameRequest = requestAnimationFrame(this._collectFps);
  };

  private _setVisible(visible: boolean) {
    this._visible = visible;
    if (visible) this._startFpsCounter();
    else this._stopFpsCounter();
  }

  private _startFpsCounter() {
    if (this._fpsFrameRequest) return;
    this._fpsFrameTimes = [];
    this._lastFpsUpdateAt = 0;
    this._currentFps = 0;
    this._fpsFrameRequest = requestAnimationFrame(this._collectFps);
  }

  private _stopFpsCounter() {
    cancelAnimationFrame(this._fpsFrameRequest);
    this._fpsFrameRequest = 0;
    this._fpsFrameTimes = [];
    this._lastFpsUpdateAt = 0;
    this._currentFps = 0;
  }

  private _captureContent(): ContentSnapshot {
    const block = this.block as
      | (HTMLElement & {
          gfx?: {
            grid: {
              search: (
                bound: unknown,
                options: { filter: string[]; useSet: boolean }
              ) => Iterable<{ flavour?: string; type?: string }>;
            };
            viewport: {
              height: number;
              viewportBounds: unknown;
              width: number;
              zoom: number;
            };
          };
          surfaceBlockModel?: {
            elementModels: Array<{ type: string }>;
          };
        })
      | null;
    const blockFlavours: Record<string, number> = {};
    const visit = (model: BlockModel) => {
      blockFlavours[model.flavour] = (blockFlavours[model.flavour] ?? 0) + 1;
      model.children.forEach(visit);
    };
    visit(this.model);

    const surfaceModels = block?.surfaceBlockModel?.elementModels ?? [];
    const surfaceElementTypes: Record<string, number> = {};
    surfaceModels.forEach(model => {
      surfaceElementTypes[model.type] =
        (surfaceElementTypes[model.type] ?? 0) + 1;
    });

    const viewport = block?.gfx?.viewport;
    const visibleElements = viewport
      ? Array.from(
          block.gfx?.grid.search(viewport.viewportBounds, {
            filter: ['block', 'canvas', 'local'],
            useSet: true,
          }) ?? []
        ).length
      : 0;

    const devicePixelRatio = window.devicePixelRatio;
    const viewportRect = block?.getBoundingClientRect();
    const domBlocks = Array.from(
      block?.querySelectorAll<HTMLElement>('[data-block-id]') ?? []
    )
      .map(element => {
        const blockId = element.dataset.blockId ?? 'unknown';
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        const descendantBlocks = Array.from(
          element.querySelectorAll<HTMLElement>('[data-block-id]')
        );
        const parentBlock =
          element.parentElement?.closest<HTMLElement>('[data-block-id]');
        const visibleWidth = viewportRect
          ? Math.max(
              0,
              Math.min(rect.right, viewportRect.right) -
                Math.max(rect.left, viewportRect.left)
            )
          : 0;
        const visibleHeight = viewportRect
          ? Math.max(
              0,
              Math.min(rect.bottom, viewportRect.bottom) -
                Math.max(rect.top, viewportRect.top)
            )
          : 0;
        const visibleCssPixels = visibleWidth * visibleHeight;
        const visibleDescendantBlocks = descendantBlocks
          .map(descendant => {
            const descendantRect = descendant.getBoundingClientRect();
            const descendantStyle = getComputedStyle(descendant);
            const descendantVisibleWidth = viewportRect
              ? Math.max(
                  0,
                  Math.min(descendantRect.right, viewportRect.right) -
                    Math.max(descendantRect.left, viewportRect.left)
                )
              : 0;
            const descendantVisibleHeight = viewportRect
              ? Math.max(
                  0,
                  Math.min(descendantRect.bottom, viewportRect.bottom) -
                    Math.max(descendantRect.top, viewportRect.top)
                )
              : 0;
            return {
              pixels: descendantVisibleWidth * descendantVisibleHeight,
              visible:
                descendantStyle.display !== 'none' &&
                descendantStyle.visibility !== 'hidden' &&
                descendantVisibleWidth > 0 &&
                descendantVisibleHeight > 0,
            };
          })
          .filter(descendant => descendant.visible);
        const ownBoxVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          rect.width > 0 &&
          rect.height > 0 &&
          visibleCssPixels > 0;
        return {
          blockId,
          descendantVisibleCssPixelsSum: Math.round(
            visibleDescendantBlocks.reduce(
              (sum, descendant) => sum + descendant.pixels,
              0
            )
          ),
          descendantBlockCount: descendantBlocks.length,
          descendants: element.querySelectorAll('*').length,
          flavour:
            this.std.store.getModelById(blockId)?.flavour ??
            element.dataset.flavour ??
            null,
          height: rounded(rect.height),
          ownBoxVisible,
          parentBlockId: parentBlock?.dataset.blockId ?? null,
          rendered: ownBoxVisible || visibleDescendantBlocks.length > 0,
          styles: {
            backfaceVisibility: style.backfaceVisibility,
            boxShadow: style.boxShadow,
            contain: style.contain,
            contentVisibility: style.contentVisibility,
            display: style.display,
            filter: style.filter,
            isolation: style.isolation,
            opacity: style.opacity,
            overflow: style.overflow,
            position: style.position,
            transform: style.transform,
            transformOrigin: style.transformOrigin,
            visibility: style.visibility,
            willChange: style.willChange,
            zIndex: style.zIndex,
          },
          tagName: element.tagName.toLowerCase(),
          visibleDescendantBlockCount: visibleDescendantBlocks.length,
          visibleCssPixels: Math.round(visibleCssPixels),
          width: rounded(rect.width),
        };
      })
      .sort((left, right) => right.visibleCssPixels - left.visibleCssPixels);
    const databaseSurfaces = Array.from(
      block?.querySelectorAll<HTMLElement>('affine-edgeless-database') ?? []
    )
      .map(database => {
        const rect = database.getBoundingClientRect();
        const databaseStyle = getComputedStyle(database);
        const descendants = Array.from(database.querySelectorAll('*'));
        const descendantTagCounts: Record<string, number> = {};
        const descendantClassCounts: Record<string, number> = {};
        descendants.forEach(element => {
          const tagName = element.tagName.toLowerCase();
          descendantTagCounts[tagName] =
            (descendantTagCounts[tagName] ?? 0) + 1;
          element.classList.forEach(className => {
            descendantClassCounts[className] =
              (descendantClassCounts[className] ?? 0) + 1;
          });
        });
        const projectedCssPixels = Math.max(0, rect.width * rect.height);
        const visibleWidth = viewportRect
          ? Math.max(
              0,
              Math.min(rect.right, viewportRect.right) -
                Math.max(rect.left, viewportRect.left)
            )
          : 0;
        const visibleHeight = viewportRect
          ? Math.max(
              0,
              Math.min(rect.bottom, viewportRect.bottom) -
                Math.max(rect.top, viewportRect.top)
            )
          : 0;
        const visibleCssPixels = visibleWidth * visibleHeight;
        let transformedElements = 0;
        let willChangeElements = 0;
        descendants.forEach(element => {
          const style = getComputedStyle(element);
          if (style.transform !== 'none') transformedElements += 1;
          if (style.willChange !== 'auto') willChangeElements += 1;
        });
        const tableCells = Array.from(
          database.querySelectorAll<HTMLElement>(
            '[data-database-cell-frame="true"]'
          )
        );

        return {
          active: database.classList.contains('block-active'),
          blockId: database.dataset.blockId ?? null,
          containment: databaseStyle.contain,
          descendants: descendants.length,
          descendantClassCounts,
          descendantTagCounts,
          projectedCssPixels: Math.round(projectedCssPixels),
          projectedDevicePixels: Math.round(
            projectedCssPixels * devicePixelRatio ** 2
          ),
          renderedCalendarEvents: database.querySelectorAll(
            '[data-calendar-event="true"]'
          ).length,
          renderedGanttRows: new Set(
            Array.from(
              database.querySelectorAll<HTMLElement>(
                'dv-gantt-view-ui [data-row-id]'
              )
            ).map(element => element.dataset.rowId)
          ).size,
          renderedKanbanCards: database.querySelectorAll(
            'affine-data-view-kanban-card'
          ).length,
          renderedKanbanCells: database.querySelectorAll(
            'affine-data-view-kanban-cell'
          ).length,
          renderedKanbanGroups: database.querySelectorAll(
            'affine-data-view-kanban-group'
          ).length,
          renderedTableCells: tableCells.length,
          renderedTableColumns: new Set(
            tableCells.map(element => element.dataset.columnId)
          ).size,
          renderedTableRows: new Set(
            tableCells.map(element => element.dataset.rowId)
          ).size,
          transformedElements,
          visibleCssPixels: Math.round(visibleCssPixels),
          visibleDevicePixels: Math.round(
            visibleCssPixels * devicePixelRatio ** 2
          ),
          willChangeElements,
        };
      })
      .sort((left, right) => right.descendants - left.descendants);

    const domSurfaces = Array.from(
      block?.querySelectorAll<HTMLElement>(GFX_DOM_SURFACE_SELECTOR) ?? []
    )
      .map(element => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        const projectedCssPixels = Math.max(0, rect.width * rect.height);
        const visibleWidth = viewportRect
          ? Math.max(
              0,
              Math.min(rect.right, viewportRect.right) -
                Math.max(rect.left, viewportRect.left)
            )
          : 0;
        const visibleHeight = viewportRect
          ? Math.max(
              0,
              Math.min(rect.bottom, viewportRect.bottom) -
                Math.max(rect.top, viewportRect.top)
            )
          : 0;
        const visibleCssPixels = visibleWidth * visibleHeight;
        const images = Array.from(element.querySelectorAll('img'));
        const logicalWidth = element.offsetWidth;
        const logicalHeight = element.offsetHeight;
        const scrollWidth = element.scrollWidth;
        const scrollHeight = element.scrollHeight;

        return {
          active: element.classList.contains('block-active'),
          blockId: element.dataset.blockId ?? null,
          boxShadow: style.boxShadow,
          canvasDescendants: element.querySelectorAll('canvas').length,
          containment: style.contain,
          contentVisibility: style.contentVisibility,
          descendants: element.querySelectorAll('*').length,
          display: style.display,
          filter: style.filter,
          iframeDescendants: element.querySelectorAll('iframe').length,
          imageDescendants: images.length,
          loadedImageDescendants: images.filter(image => image.complete).length,
          logicalCssPixels: logicalWidth * logicalHeight,
          logicalHeight,
          logicalWidth,
          naturalImagePixels: images.reduce(
            (sum, image) => sum + image.naturalWidth * image.naturalHeight,
            0
          ),
          opacity: style.opacity,
          overflow: style.overflow,
          projectedCssPixels: Math.round(projectedCssPixels),
          projectedDevicePixels: Math.round(
            projectedCssPixels * devicePixelRatio ** 2
          ),
          scrollCssPixels: scrollWidth * scrollHeight,
          scrollHeight,
          scrollWidth,
          svgDescendants: element.querySelectorAll('svg').length,
          tagName: element.tagName.toLowerCase(),
          textCharacters: element.textContent?.length ?? 0,
          transform: style.transform,
          visibleCssPixels: Math.round(visibleCssPixels),
          visibleDevicePixels: Math.round(
            visibleCssPixels * devicePixelRatio ** 2
          ),
          willChange: style.willChange,
        };
      })
      .sort(
        (left, right) => right.visibleDevicePixels - left.visibleDevicePixels
      );

    return {
      blockFlavours,
      databaseSurfaces,
      domBlocks,
      domSurfaces,
      domElements: block?.querySelectorAll('*').length ?? 0,
      surfaceElementTypes,
      surfaceElements: surfaceModels.length,
      visibleElements,
      viewport: {
        height: Math.round(viewport?.height ?? 0),
        width: Math.round(viewport?.width ?? 0),
        zoom: rounded(viewport?.zoom ?? 0, 3),
      },
    };
  }

  private readonly _collectFrame = (timestamp: number) => {
    if (!this._recording) return;
    if (this._lastFrameAt > 0) {
      const duration = timestamp - this._lastFrameAt;
      addSample(this._frameSamples, duration);
      this._recordFrameContext(duration, timestamp);
    }
    this._lastFrameAt = timestamp;
    if (timestamp - this._lastUiUpdateAt >= 500) {
      this._elapsedMs = timestamp - this._sessionStartedAt;
      this._lastUiUpdateAt = timestamp;
    }
    this._frameRequest = requestAnimationFrame(this._collectFrame);
  };

  private _recordFrameContext(duration: number, timestamp: number) {
    const viewport = (
      this.block as
        | (HTMLElement & {
            gfx?: {
              viewport?: {
                panning$?: { value: boolean };
                zoom: number;
                zooming$?: { value: boolean };
              };
            };
          })
        | null
    )?.gfx?.viewport;
    const interaction = viewport?.zooming$?.value
      ? 'zoom'
      : viewport?.panning$?.value
        ? 'pan'
        : 'idle';
    addBucketSample(this._frameInteractionBuckets, interaction, duration);
    addBucketSample(
      this._frameVisibleBuckets,
      visibleElementsBucket(this._latestVisibleItems),
      duration
    );
    addBucketSample(
      this._frameZoomBuckets,
      zoomBucket(viewport?.zoom ?? 0),
      duration
    );
    const blockProbeSettled = this._blockProbePhase !== null;
    this._frameTimeline.push({
      atMs: rounded(timestamp - this._sessionStartedAt),
      blockProbePhase: this._blockProbePhase,
      blockProbeSettled,
      durationMs: rounded(duration),
      interaction,
      visibleElements: this._latestVisibleItems,
      zoom: rounded(viewport?.zoom ?? 0, 3),
    });
  }

  private _isEdgeless() {
    return this.block?.tagName === 'AFFINE-EDGELESS-ROOT';
  }

  private _isGfxEditing() {
    return Boolean(
      (
        this.block as
          | (HTMLElement & { gfx?: { selection?: { editing?: boolean } } })
          | null
      )?.gfx?.selection?.editing
    );
  }

  private readonly _onPerformanceSample = (sample: GfxPerformanceSample) => {
    if (!this._recording || sample.scope !== this.std) return;
    const collectorStartedAt = performance.now();
    try {
      this._rawGfxSampleCount += 1;
      const rawSample: RawGfxSample = {
        atMs: rounded(performance.now() - this._sessionStartedAt, 3),
        detail: sample.detail,
        durationMs: rounded(sample.durationMs, 4),
        itemCount: sample.itemCount,
        phase: sample.phase,
      };
      if (this._rawGfxSamples.length < MAX_RAW_GFX_SAMPLES)
        this._rawGfxSamples.push(rawSample);

      const detailKey = `${sample.phase}|${JSON.stringify(sample.detail ?? {})}`;
      const detailSeries = this._detailedGfxSamples.get(detailKey) ?? {
        detail: sample.detail,
        durations: [],
        itemCountMax: 0,
        itemCountTotal: 0,
        phase: sample.phase,
      };
      addSample(detailSeries.durations, sample.durationMs);
      detailSeries.itemCountMax = Math.max(
        detailSeries.itemCountMax,
        sample.itemCount ?? 0
      );
      detailSeries.itemCountTotal += sample.itemCount ?? 0;
      this._detailedGfxSamples.set(detailKey, detailSeries);

      if (
        sample.phase === 'canvas-element-render' ||
        sample.phase === 'canvas-element-visibility'
      ) {
        const elementId = String(sample.detail?.elementId ?? 'unknown');
        const elementType = String(sample.detail?.elementType ?? 'unknown');
        const element = this._canvasElementSamples.get(elementId) ?? {
          elementId,
          elementType,
          renderDurations: [],
          visibilityDurations: [],
          visibilityChecks: 0,
          visibleChecks: 0,
        };
        if (sample.phase === 'canvas-element-render') {
          addSample(element.renderDurations, sample.durationMs);
        } else {
          addSample(element.visibilityDurations, sample.durationMs);
          element.visibilityChecks += 1;
          if (sample.detail?.visible === true) element.visibleChecks += 1;
        }
        this._canvasElementSamples.set(elementId, element);
      }

      if (
        sample.phase === 'block-lit-update' ||
        sample.phase === 'viewport-dom-transform'
      ) {
        const blockId = String(sample.detail?.blockId ?? 'unknown');
        const block = this._getBlockRuntimeSeries(blockId, sample.detail);
        if (sample.phase === 'block-lit-update') {
          addSample(block.litUpdateDurations, sample.durationMs);
          const reasons = String(sample.detail?.updateReasons ?? 'unknown');
          block.updateReasons[reasons] =
            (block.updateReasons[reasons] ?? 0) + 1;
        } else {
          addSample(block.transformDurations, sample.durationMs);
        }
      }

      if (sample.phase === 'viewport-cull' && sample.itemCount !== undefined)
        this._latestVisibleItems = sample.itemCount;
      const series = this._renderSamples.get(sample.phase) ?? {
        durations: [],
        itemCountMax: 0,
        itemCountTotal: 0,
      };
      addSample(series.durations, sample.durationMs);
      series.itemCountMax = Math.max(
        series.itemCountMax,
        sample.itemCount ?? 0
      );
      series.itemCountTotal += sample.itemCount ?? 0;
      this._renderSamples.set(sample.phase, series);
    } finally {
      addSample(
        this._gfxCollectorDurations,
        performance.now() - collectorStartedAt
      );
    }
  };

  private _getBlockRuntimeSeries(
    blockId: string,
    detail?: GfxPerformanceSample['detail']
  ) {
    const current = this._blockRuntimeSamples.get(blockId);
    if (current) {
      if (!current.flavour && typeof detail?.flavour === 'string')
        current.flavour = detail.flavour;
      if (!current.tagName && typeof detail?.tagName === 'string')
        current.tagName = detail.tagName;
      if (!current.parentBlockId && typeof detail?.parentBlockId === 'string')
        current.parentBlockId = detail.parentBlockId;
      return current;
    }
    const created: BlockRuntimeSeries = {
      attributeMutations: 0,
      blockId,
      childListMutations: 0,
      descendantMutations: 0,
      flavour: typeof detail?.flavour === 'string' ? detail.flavour : null,
      litUpdateDurations: [],
      mutationTargets: {},
      parentBlockId:
        typeof detail?.parentBlockId === 'string' ? detail.parentBlockId : null,
      selfMutations: 0,
      stylePropertiesChanged: {},
      stylePropertiesObserved: {},
      styleMutations: 0,
      tagName: typeof detail?.tagName === 'string' ? detail.tagName : null,
      transformDurations: [],
      updateReasons: {},
    };
    this._blockRuntimeSamples.set(blockId, created);
    return created;
  }

  private _cancelPanTestTimers() {
    window.clearTimeout(this._panTestStopTimeout);
    cancelAnimationFrame(this._panTestAnimationFrame);
    cancelAnimationFrame(this._settleAnimationFrame);
    this._panTestStopTimeout = 0;
    this._panTestAnimationFrame = 0;
    this._settleAnimationFrame = 0;
  }

  private _getViewport() {
    return (
      this.block as
        | (HTMLElement & {
            gfx?: {
              viewport?: {
                centerX: number;
                centerY: number;
                panning$?: { value: boolean };
                setCenter: (centerX: number, centerY: number) => void;
                zoom: number;
                zooming$?: { value: boolean };
              };
            };
          })
        | null
    )?.gfx?.viewport;
  }

  private _prepareBlockProbe() {
    const root = this.block;
    if (!root) return false;
    const domTargets = Array.from(
      root.querySelectorAll<HTMLElement>(GFX_DOM_SURFACE_SELECTOR)
    ).filter(element => {
      const parentBlock =
        element.parentElement?.closest<HTMLElement>('[data-block-id]');
      return parentBlock?.dataset.blockId === this.model.id;
    });
    if (domTargets.length === 0) return false;
    const domTargetInventory = domTargets.map(element => {
      const parentBlock =
        element.parentElement?.closest<HTMLElement>('[data-block-id]');
      const rect = element.getBoundingClientRect();
      const descendantBlocks = Array.from(
        element.querySelectorAll<HTMLElement>('[data-block-id]')
      );
      const visibleDescendantBlockCount = descendantBlocks.filter(block => {
        const descendantRect = block.getBoundingClientRect();
        const style = getComputedStyle(block);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          descendantRect.width > 0 &&
          descendantRect.height > 0
        );
      }).length;
      const images = Array.from(element.querySelectorAll('img'));
      return {
        blockId: element.dataset.blockId ?? 'unknown',
        descendantBlockCount: descendantBlocks.length,
        descendants: element.querySelectorAll('*').length,
        flavour:
          this.std.store.getModelById(element.dataset.blockId ?? '')?.flavour ??
          null,
        naturalImagePixels: images.reduce(
          (sum, image) => sum + image.naturalWidth * image.naturalHeight,
          0
        ),
        ownHeight: rounded(rect.height),
        ownWidth: rounded(rect.width),
        parentBlockId: parentBlock?.dataset.blockId ?? null,
        tagName: element.tagName.toLowerCase(),
        textCharacters: element.textContent?.length ?? 0,
        visibleDescendantBlockCount,
      };
    });
    const selectedTarget = [...domTargets].sort(
      (left, right) =>
        right.querySelectorAll('*').length - left.querySelectorAll('*').length
    )[0];
    if (!selectedTarget) return false;
    const selectedInventory = domTargetInventory.find(
      target => target.blockId === selectedTarget.dataset.blockId
    );
    const nestedCandidates = Array.from(
      selectedTarget.querySelectorAll<HTMLElement>('[data-block-id]')
    )
      .filter(element => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return (
          Boolean(element.dataset.blockId) &&
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          rect.width > 0 &&
          rect.height > 0
        );
      })
      .map(element => ({
        element,
        info: {
          blockId: element.dataset.blockId ?? 'unknown',
          descendantBlockCount:
            element.querySelectorAll('[data-block-id]').length,
          descendants: element.querySelectorAll('*').length,
          flavour:
            this.std.store.getModelById(element.dataset.blockId ?? '')
              ?.flavour ?? null,
          tagName: element.tagName.toLowerCase(),
          textCharacters: element.textContent?.length ?? 0,
        },
      }))
      .sort(
        (left, right) =>
          Number(right.info.descendants) - Number(left.info.descendants)
      );
    this._blockProbeRootTarget = selectedTarget;
    this._blockProbeTopLevelDomTargets = domTargets;
    this._blockProbeCandidates = nestedCandidates;
    this._blockProbeTopLevelDatabaseTargets = domTargets
      .filter(element => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return (
          element.matches('affine-edgeless-database') &&
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          rect.width > 0 &&
          rect.height > 0
        );
      })
      .sort(
        (left, right) =>
          right.querySelectorAll('*').length - left.querySelectorAll('*').length
      );
    this._blockProbeDomTargets = [selectedTarget];
    this._blockProbeTargetInfo = {
      candidateCount: domTargetInventory.length,
      topLevelDatabases: this._blockProbeTopLevelDatabaseTargets.map(
        element => ({
          blockId: element.dataset.blockId ?? 'unknown',
          descendants: element.querySelectorAll('*').length,
        })
      ),
      nestedCandidates: nestedCandidates.slice(0, 12).map(({ info }) => info),
      selectionRule: 'largest-top-level-dom-gfx-subtree-by-descendant-count',
      target: selectedInventory ?? null,
    };
    return true;
  }

  private _applyBlockProbeScenario(scenario: DiagnosticScenario) {
    this._clearBlockProbePresentation();
    this._activeDiagnosticScenario = scenario;
    const knownTargets = [
      this._blockProbeRootTarget,
      ...this._blockProbeTopLevelDomTargets,
      ...this._blockProbeTopLevelDatabaseTargets,
      ...this._blockProbeCandidates.map(entry => entry.element),
    ].filter((target): target is HTMLElement => target !== null);
    const targetIds =
      scenario.targetBlockIds ??
      (scenario.targetBlockId ? [scenario.targetBlockId] : []);
    this._blockProbeDomTargets = targetIds.length
      ? targetIds
          .map(id => knownTargets.find(target => target.dataset.blockId === id))
          .filter((target): target is HTMLElement => target !== undefined)
      : this._blockProbeRootTarget
        ? [this._blockProbeRootTarget]
        : [];
    this._blockProbePhase = scenario.phase;
    if (scenario.forceLayoutAfterViewportTask) {
      document.documentElement.dataset.streamlainPerformanceForceViewportTaskLayout =
        'true';
    }
    if (scenario.suspendRemoteSelectionViewportWrites) {
      document.documentElement.dataset.streamlainPerformanceSuspendRemoteSelectionViewport =
        'true';
    }
    if (scenario.suspendViewportBackgroundWrites) {
      document.documentElement.dataset.streamlainPerformanceSuspendViewportBackground =
        'true';
    }
    if (scenario.suspendTransformWrites) {
      this._blockProbeDomTargets.forEach(target => {
        target.dataset.streamlainPerformanceSuspend = 'true';
      });
    }
    if (!scenario.css) return;
    this._blockProbeStyle = document.createElement('style');
    const descendantSelector = scenario.descendantSelector
      ? ` ${scenario.descendantSelector}`
      : '';
    this._blockProbeStyle.textContent = `[${BLOCK_PROBE_ATTRIBUTE}='true']${descendantSelector} { ${scenario.css} }`;
    document.head.append(this._blockProbeStyle);
    this._blockProbeDomTargets.forEach(target =>
      target.setAttribute(BLOCK_PROBE_ATTRIBUTE, 'true')
    );
  }

  private _clearBlockProbePresentation() {
    delete document.documentElement.dataset
      .streamlainPerformanceForceViewportTaskLayout;
    delete document.documentElement.dataset
      .streamlainPerformanceSuspendRemoteSelectionViewport;
    delete document.documentElement.dataset
      .streamlainPerformanceSuspendViewportBackground;
    this._blockProbeDomTargets.forEach(target =>
      target.removeAttribute(BLOCK_PROBE_ATTRIBUTE)
    );
    this._blockProbeDomTargets.forEach(target => {
      delete target.dataset.streamlainPerformanceSuspend;
    });
    this._blockProbeStyle?.remove();
    this._blockProbePhase = null;
    this._blockProbeStyle = null;
  }

  private _restoreBlockProbe() {
    this._clearBlockProbePresentation();
    this._blockProbeDomTargets = [];
    this._blockProbeCandidates = [];
    this._blockProbeTopLevelDomTargets = [];
    this._blockProbeTopLevelDatabaseTargets = [];
    this._blockProbeRootTarget = null;
    this._activeDiagnosticScenario = null;
  }

  private _waitForStableCadence(onSettled: () => void) {
    const startedAt = performance.now();
    const timestamps: number[] = [];
    let lastCheckedAt = startedAt;
    let stableChecks = 0;

    const finish = (
      reason: SettleEvidence['reason'],
      previous: number[],
      recent: number[]
    ) => {
      this._settleAnimationFrame = 0;
      this._settleEvidence = {
        durationMs: rounded(performance.now() - startedAt),
        previous: summarize(previous),
        reason,
        recent: summarize(recent),
        stableChecks,
      };
      onSettled();
    };

    const collect = (timestamp: number) => {
      if (!this._armed) return;
      timestamps.push(timestamp);
      while (timestamps[0] < timestamp - SETTLE_WINDOW_MS * 2.2) {
        timestamps.shift();
      }

      const elapsed = timestamp - startedAt;
      this._countdownMs = elapsed;
      if (timestamp - lastCheckedAt >= SETTLE_CHECK_INTERVAL_MS) {
        lastCheckedAt = timestamp;
        const previous = frameDurationsFromTimestamps(
          timestamps.filter(
            value =>
              value >= timestamp - SETTLE_WINDOW_MS * 2 &&
              value < timestamp - SETTLE_WINDOW_MS
          )
        );
        const recent = frameDurationsFromTimestamps(
          timestamps.filter(value => value >= timestamp - SETTLE_WINDOW_MS)
        );
        const previousSummary = summarize(previous);
        const recentSummary = summarize(recent);
        const p50Tolerance = Math.max(1, previousSummary.p50Ms * 0.05);
        const p95Tolerance = Math.max(2, previousSummary.p95Ms * 0.1);
        const stable =
          previous.length >= 10 &&
          recent.length >= 10 &&
          Math.abs(recentSummary.p50Ms - previousSummary.p50Ms) <=
            p50Tolerance &&
          Math.abs(recentSummary.p95Ms - previousSummary.p95Ms) <= p95Tolerance;
        stableChecks = stable ? stableChecks + 1 : 0;

        if (
          elapsed >= SETTLE_MIN_MS &&
          stableChecks >= SETTLE_REQUIRED_CHECKS
        ) {
          finish('cadence-stable', previous, recent);
          return;
        }
        if (elapsed >= SETTLE_MAX_MS) {
          finish('timeout', previous, recent);
          return;
        }
      }
      this._settleAnimationFrame = requestAnimationFrame(collect);
    };

    this._settleAnimationFrame = requestAnimationFrame(collect);
  }

  private readonly _animatePanTest = (timestamp: number) => {
    const callbackStartedAt = performance.now();
    if (!this._recording || !this._panTestInitialViewport) return;
    const viewport = this._getViewport();
    if (!viewport) return;
    const elapsed = Math.min(
      PAN_TEST_DURATION_MS,
      timestamp - this._panTestAnimationStartedAt
    );
    const phase = (elapsed / PAN_TEST_DURATION_MS) * Math.PI * 2;
    const { centerX, centerY, zoom } = this._panTestInitialViewport;
    const next = {
      x: centerX + (Math.sin(phase) * PAN_TEST_HORIZONTAL_AMPLITUDE_PX) / zoom,
      y:
        centerY + (Math.sin(phase * 2) * PAN_TEST_VERTICAL_AMPLITUDE_PX) / zoom,
    };
    const last = this._panTestLastCenter ?? { x: centerX, y: centerY };
    this._panTestDistanceCssPx +=
      Math.hypot(next.x - last.x, next.y - last.y) * zoom;
    this._panTestLastCenter = next;
    this._panTestMovementUpdates += 1;
    viewport.setCenter(next.x, next.y);
    addSample(this._panDriverDurations, performance.now() - callbackStartedAt);
    this._panTestAnimationFrame = requestAnimationFrame(this._animatePanTest);
  };

  private _startAutomatedPanTest() {
    const viewport = this._getViewport();
    if (!viewport) return false;
    this._panTestInitialViewport = {
      centerX: viewport.centerX,
      centerY: viewport.centerY,
      zoom: viewport.zoom,
    };
    this._panTestLastCenter = {
      x: viewport.centerX,
      y: viewport.centerY,
    };
    this._panTestDistanceCssPx = 0;
    this._panTestMovementUpdates = 0;
    this._panTestAnimationStartedAt = performance.now();
    this._panTestAnimationFrame = requestAnimationFrame(this._animatePanTest);
    return true;
  }

  private _restorePanTestViewport() {
    const initial = this._panTestInitialViewport;
    this._panTestInitialViewport = null;
    this._panTestLastCenter = null;
    if (!initial) return;
    this._getViewport()?.setCenter(initial.centerX, initial.centerY);
  }

  private _cdpCaptureBinding() {
    return (
      window as Window & {
        streamlainCdpCapture?: CdpCaptureBinding;
      }
    ).streamlainCdpCapture;
  }

  private async _beginCdpCapture() {
    const binding = this._cdpCaptureBinding();
    if (!binding) {
      this._cdpCaptureError =
        'Chrome-CDP-Begleiter fehlt. Kein diagnostischer Lauf gestartet.';
      return false;
    }
    const captureId = crypto.randomUUID();
    this._cdpCaptureId = captureId;
    this._cdpCaptureEntries = {};
    try {
      const response = await binding({ action: 'start', captureId });
      if (response.status !== 'started') {
        throw new Error(response.error ?? 'cdp-start-failed');
      }
      return true;
    } catch {
      this._cdpCaptureId = null;
      this._cdpCaptureError = 'Chrome-CDP-Trace konnte nicht gestartet werden.';
      return false;
    }
  }

  private async _finishCdpCapture() {
    const binding = this._cdpCaptureBinding();
    const captureId = this._cdpCaptureId;
    this._cdpCaptureId = null;
    if (!binding || !captureId) {
      this._cdpCaptureError = 'Chrome-CDP-Trace wurde nicht aufgezeichnet.';
      return false;
    }
    try {
      const response = await binding({ action: 'stop', captureId });
      if (response.status !== 'stopped' || !response.bundleBase64) {
        throw new Error(response.error ?? 'cdp-stop-failed');
      }
      const binary = atob(response.bundleBase64);
      const bytes = Uint8Array.from(binary, character =>
        character.charCodeAt(0)
      );
      this._cdpCaptureEntries = unzipSync(bytes);
      return true;
    } catch {
      this._cdpCaptureEntries = {};
      this._cdpCaptureError =
        'Chrome-CDP-Trace konnte nicht abgeschlossen werden.';
      return false;
    }
  }

  private async _finishAutomatedPanTest() {
    if (!this._recording) return;
    cancelAnimationFrame(this._panTestAnimationFrame);
    this._panTestAnimationFrame = 0;
    this._restorePanTestViewport();
    this._stop();
    if (this._panTestMode === 'diagnostic-suite') {
      this._captureDiagnosticSuiteSegment();
      this._diagnosticSuiteScenarioIndex += 1;
      this._runNextDiagnosticSuiteScenario();
      return;
    }
    this._restoreBlockProbe();
    const cdpCaptureSucceeded = this._panTestCapturesCdp
      ? await this._finishCdpCapture()
      : true;
    this._panTestCapturesCdp = false;
    if (cdpCaptureSucceeded) {
      await this._saveBundleAndCopyPath();
    }
  }

  private readonly _onCdpCaptureEnabledChange = (event: Event) => {
    this._cdpCaptureEnabled = (event.currentTarget as HTMLInputElement).checked;
    this._cdpCaptureError = '';
  };

  private readonly _runBenchmark = () => this._runPanTest('benchmark');

  private readonly _runDiagnosticSuite = () => {
    if (this._recording || this._armed || !this._isEdgeless()) return;
    this._cancelPanTestTimers();
    this._restoreBlockProbe();
    this._blockProbeTargetInfo = null;
    if (!this._prepareBlockProbe()) return;
    const rootBlockId = this._blockProbeRootTarget?.dataset.blockId ?? null;
    const largestListBlockIdValue = this._blockProbeCandidates.find(
      candidate => candidate.info.flavour === 'affine:list'
    )?.info.blockId;
    const largestListBlockId =
      typeof largestListBlockIdValue === 'string'
        ? largestListBlockIdValue
        : rootBlockId;
    const databaseBlockIdValue = this._blockProbeCandidates.find(
      candidate => candidate.info.flavour === 'affine:database'
    )?.info.blockId;
    const databaseBlockId =
      typeof databaseBlockIdValue === 'string'
        ? databaseBlockIdValue
        : rootBlockId;
    const topLevelDatabaseIds = this._blockProbeTopLevelDatabaseTargets.map(
      target => target.dataset.blockId ?? 'unknown'
    );
    const databaseIsolationScenarios: DiagnosticScenario[] =
      topLevelDatabaseIds.length > 1
        ? [
            {
              css: null,
              id: 'baseline',
              label: 'Baseline',
              phase: null,
              suspendTransformWrites: false,
              targetBlockId: rootBlockId,
              visualPresentation: 'retained',
            },
            {
              css: 'will-change: transform !important;',
              id: 'all-databases-transform-promoted',
              label: 'Database hosts promoted',
              phase: 'style-variant',
              suspendTransformWrites: false,
              targetBlockId: null,
              targetBlockIds: topLevelDatabaseIds,
              visualPresentation: 'retained',
            },
            {
              css: 'will-change: transform !important;',
              descendantSelector:
                '> [data-testid="edgeless-database-container"]',
              id: 'all-database-shells-promoted',
              label: 'Database shells promoted',
              phase: 'style-variant',
              suspendTransformWrites: false,
              targetBlockId: null,
              targetBlockIds: topLevelDatabaseIds,
              visualPresentation: 'retained',
            },
            {
              css: 'will-change: transform !important;',
              descendantSelector:
                '> [data-testid="edgeless-database-container"] > [data-testid="edgeless-database-clip-container"]',
              id: 'all-database-clips-promoted',
              label: 'Database clips promoted',
              phase: 'style-variant',
              suspendTransformWrites: false,
              targetBlockId: null,
              targetBlockIds: topLevelDatabaseIds,
              visualPresentation: 'retained',
            },
            {
              css: 'will-change: transform !important;',
              descendantSelector: '.edgeless-database-content',
              id: 'all-database-content-promoted',
              label: 'Database content promoted',
              phase: 'style-variant',
              suspendTransformWrites: false,
              targetBlockId: null,
              targetBlockIds: topLevelDatabaseIds,
              visualPresentation: 'retained',
            },
            {
              css: null,
              id: 'baseline-repeat',
              label: 'Baseline repeat',
              phase: null,
              suspendTransformWrites: false,
              targetBlockId: rootBlockId,
              visualPresentation: 'retained',
            },
          ]
        : [];
    this._diagnosticScenarios = databaseIsolationScenarios.length
      ? databaseIsolationScenarios
      : [
          {
            css: null,
            id: 'baseline',
            label: 'Baseline',
            phase: null,
            suspendTransformWrites: false,
            targetBlockId: rootBlockId,
            visualPresentation: 'retained',
          },
          {
            css: 'contain: size layout style paint !important; isolation: isolate !important;',
            id: 'note-paint-contained',
            label: 'Note paint contained',
            phase: 'style-variant',
            suspendTransformWrites: false,
            targetBlockId: rootBlockId,
            visualPresentation: 'retained',
          },
          {
            css: 'contain: layout style paint !important; isolation: isolate !important;',
            id: 'largest-list-paint-contained',
            label: 'Largest list paint contained',
            phase: 'style-variant',
            suspendTransformWrites: false,
            targetBlockId: largestListBlockId,
            visualPresentation: 'retained',
          },
          {
            css: 'contain: layout style paint !important; isolation: isolate !important;',
            id: 'database-paint-contained',
            label: 'Database paint contained',
            phase: 'style-variant',
            suspendTransformWrites: false,
            targetBlockId: databaseBlockId,
            visualPresentation: 'retained',
          },
          {
            css: 'will-change: transform !important;',
            id: 'largest-block-transform-promoted',
            label: 'Largest block transform promoted',
            phase: 'style-variant',
            suspendTransformWrites: false,
            targetBlockId: largestListBlockId,
            visualPresentation: 'retained',
          },
          {
            css: null,
            id: 'baseline-repeat',
            label: 'Baseline repeat',
            phase: null,
            suspendTransformWrites: false,
            targetBlockId: rootBlockId,
            visualPresentation: 'retained',
          },
        ];
    this._copied = false;
    this._copyFailed = false;
    this._storedCapturePath = '';
    this._report = '';
    this._reportValid = null;
    this._diagnosticSuiteEntries = {};
    this._diagnosticSuiteReports = [];
    this._diagnosticSuiteScenarioIndex = 0;
    this._panTestMode = 'diagnostic-suite';
    this._runNextDiagnosticSuiteScenario();
  };

  private _runNextDiagnosticSuiteScenario() {
    const scenario =
      this._diagnosticScenarios[this._diagnosticSuiteScenarioIndex];
    if (!scenario) {
      this._clearBlockProbePresentation();
      this._activeDiagnosticScenario = null;
      this._suiteProgress = '';
      void this._saveDiagnosticSuiteAndCopyPath();
      this._restoreBlockProbe();
      return;
    }
    this._applyBlockProbeScenario(scenario);
    this._suiteProgress = `${this._diagnosticSuiteScenarioIndex + 1}/${this._diagnosticScenarios.length} · ${scenario.label}`;
    this._settleEvidence = null;
    this._armed = true;
    this._countdownMs = 0;
    this._waitForStableCadence(() => {
      this._countdownMs = 0;
      this._armed = false;
      this._start();
      if (!this._startAutomatedPanTest()) {
        this._stop();
        this._restoreBlockProbe();
        return;
      }
      this._panTestTargetEndAt = this._sessionStartedAt + PAN_TEST_DURATION_MS;
      this._panTestStopTimeout = window.setTimeout(() => {
        void this._finishAutomatedPanTest();
      }, PAN_TEST_DURATION_MS);
    });
  }

  private _runPanTest(mode: PanTestMode) {
    if (this._recording || this._armed || !this._isEdgeless()) return;
    this._cancelPanTestTimers();
    this._copied = false;
    this._copyFailed = false;
    this._cdpCaptureError = '';
    this._cdpCaptureEntries = {};
    this._cdpCaptureId = null;
    this._panTestCapturesCdp = false;
    this._storedCapturePath = '';
    this._report = '';
    this._reportValid = null;
    this._elapsedMs = 0;
    this._settleEvidence = null;
    this._panTestMode = mode;
    this._blockProbeTargetInfo = null;
    this._restoreBlockProbe();
    this._armed = true;
    this._countdownMs = 0;
    this._waitForStableCadence(() => {
      void (async () => {
        this._countdownMs = 0;
        this._armed = false;
        this._panTestCapturesCdp = this._cdpCaptureEnabled;
        if (this._panTestCapturesCdp && !(await this._beginCdpCapture())) {
          this._panTestCapturesCdp = false;
          return;
        }
        this._start();
        if (!this._startAutomatedPanTest()) {
          this._stop();
          this._restoreBlockProbe();
          if (this._panTestCapturesCdp) await this._finishCdpCapture();
          this._panTestCapturesCdp = false;
          return;
        }
        this._panTestTargetEndAt =
          this._sessionStartedAt + PAN_TEST_DURATION_MS;
        this._panTestStopTimeout = window.setTimeout(
          () => void this._finishAutomatedPanTest(),
          PAN_TEST_DURATION_MS
        );
      })();
    });
  }

  private _start() {
    if (this._recording || !this._isEdgeless()) return;
    this._copied = false;
    this._report = '';
    this._reportObject = null;
    this._frameSamples = [];
    this._frameTimeline = [];
    this._interactionDurations = [];
    this._interactionInputDelays = [];
    this._interactionPresentationDelays = [];
    this._interactionProcessingDurations = [];
    this._interactionEvents = {};
    this._longTaskDurations = [];
    this._longAnimationBlockingDurations = [];
    this._longAnimationDurations = [];
    this._longAnimationForcedStyleDurations = [];
    this._longAnimationRenderDurations = [];
    this._longAnimationScriptDurations = [];
    this._rawLongAnimationFrames = [];
    this._longAnimationScripts.clear();
    this._layoutShiftScores = [];
    this._unexpectedLayoutShiftScores = [];
    this._gcDurations = [];
    this._resources.clear();
    this._rawResources = [];
    this._rawGfxSamples = [];
    this._rawGfxSampleCount = 0;
    this._canvasElementSamples.clear();
    this._blockRuntimeSamples.clear();
    this._detailedGfxSamples.clear();
    this._gfxCollectorDurations = [];
    this._panDriverDurations = [];
    this._domMutations.clear();
    this._domMutationObserverDurations = [];
    this._frameInteractionBuckets.clear();
    this._frameVisibleBuckets.clear();
    this._frameZoomBuckets.clear();
    this._renderSamples.clear();
    this._recordingCadence = estimateDisplayCadence(
      frameDurationsFromTimestamps(this._fpsFrameTimes)
    );
    this._startContent = this._captureContent();
    this._latestVisibleItems = this._startContent.visibleElements;
    this._startHeapMb = this._usedHeapMb();
    this._sessionStartedAt = performance.now();
    this._sessionStartedAtEpochMs = Date.now();
    this._lastFrameAt = 0;
    this._lastUiUpdateAt = 0;
    this._recording = true;
    document.documentElement.dataset.streamlainCanvasPerformanceRecording =
      'true';
    this._unsubscribePerformance = subscribeGfxPerformanceSamples(
      this._onPerformanceSample
    );
    this._recordingAbort = new AbortController();
    const root = this.block;
    const countEvent = (event: Event) => {
      this._interactionEvents[event.type] =
        (this._interactionEvents[event.type] ?? 0) + 1;
    };
    ['keydown', 'pointerdown', 'pointermove', 'pointerup', 'wheel'].forEach(
      type =>
        root?.addEventListener(type, countEvent, {
          capture: true,
          signal: this._recordingAbort?.signal,
        })
    );

    this._domMutationObserver = new MutationObserver(records => {
      const observerStartedAt = performance.now();
      try {
        const nextStyleByRecord = new Map<MutationRecord, string | null>();
        const nextStyleByTarget = new Map<Node, string | null>();
        [...records].reverse().forEach(record => {
          if (record.type !== 'attributes' || record.attributeName !== 'style')
            return;
          const nextStyle = nextStyleByTarget.has(record.target)
            ? (nextStyleByTarget.get(record.target) ?? null)
            : record.target instanceof Element
              ? record.target.getAttribute('style')
              : null;
          nextStyleByRecord.set(record, nextStyle);
          nextStyleByTarget.set(record.target, record.oldValue);
        });
        records.forEach(record => {
          const targetElement =
            record.target instanceof Element ? record.target : null;
          const tagName = targetElement
            ? targetElement.tagName.toLowerCase()
            : record.target.nodeName.toLowerCase();
          const attributeName =
            record.type === 'attributes' ? record.attributeName : null;
          const key = `${record.type}|${tagName}|${attributeName ?? ''}`;
          const series = this._domMutations.get(key) ?? {
            addedNodes: 0,
            attributeName,
            attributeChanges: 0,
            count: 0,
            removedNodes: 0,
            tagName,
            type: record.type,
          };
          series.count += 1;
          series.addedNodes += record.addedNodes.length;
          series.removedNodes += record.removedNodes.length;
          series.attributeChanges += record.type === 'attributes' ? 1 : 0;
          this._domMutations.set(key, series);

          const owner = targetElement?.closest<HTMLElement>('[data-block-id]');
          const blockId = owner?.dataset.blockId;
          if (blockId) {
            const block = this._getBlockRuntimeSeries(blockId, {
              blockId,
              flavour: owner.dataset.flavour ?? null,
              parentBlockId:
                owner.parentElement?.closest<HTMLElement>('[data-block-id]')
                  ?.dataset.blockId ?? null,
              tagName: owner.tagName.toLowerCase(),
            });
            if (targetElement === owner) block.selfMutations += 1;
            else block.descendantMutations += 1;
            if (record.type === 'attributes') block.attributeMutations += 1;
            if (record.type === 'childList') block.childListMutations += 1;
            if (record.attributeName === 'style') block.styleMutations += 1;
            if (
              record.attributeName === 'style' &&
              targetElement instanceof HTMLElement
            ) {
              Array.from(targetElement.style).forEach(property => {
                block.stylePropertiesObserved[property] =
                  (block.stylePropertiesObserved[property] ?? 0) + 1;
              });
              changedCssProperties(
                record.oldValue,
                nextStyleByRecord.get(record) ?? null
              ).forEach(property => {
                block.stylePropertiesChanged[property] =
                  (block.stylePropertiesChanged[property] ?? 0) + 1;
              });
            }
            const mutationKey = `${record.type}|${tagName}|${attributeName ?? ''}`;
            block.mutationTargets[mutationKey] =
              (block.mutationTargets[mutationKey] ?? 0) + 1;
          }
        });
      } finally {
        addSample(
          this._domMutationObserverDurations,
          performance.now() - observerStartedAt
        );
      }
    });
    if (root) {
      this._domMutationObserver.observe(root, {
        attributes: true,
        attributeOldValue: true,
        childList: true,
        subtree: true,
      });
    }

    const supported = PerformanceObserver.supportedEntryTypes;
    if (supported.includes('longtask')) {
      const observer = new PerformanceObserver(list => {
        list
          .getEntries()
          .forEach(entry => addSample(this._longTaskDurations, entry.duration));
      });
      observer.observe({ entryTypes: ['longtask'] });
      this._performanceObservers.push(observer);
    }
    if (supported.includes('event')) {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          const timing = entry as PerformanceEntry & {
            processingEnd?: number;
            processingStart?: number;
          };
          const processingStart = timing.processingStart ?? entry.startTime;
          const processingEnd = timing.processingEnd ?? processingStart;
          addSample(this._interactionDurations, entry.duration);
          addSample(
            this._interactionInputDelays,
            Math.max(0, processingStart - entry.startTime)
          );
          addSample(
            this._interactionProcessingDurations,
            Math.max(0, processingEnd - processingStart)
          );
          addSample(
            this._interactionPresentationDelays,
            Math.max(0, entry.startTime + entry.duration - processingEnd)
          );
        });
      });
      observer.observe({
        type: 'event',
        buffered: false,
        durationThreshold: 16,
      } as PerformanceObserverInit);
      this._performanceObservers.push(observer);
    }
    if (supported.includes('long-animation-frame')) {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          const frame = entry as LongAnimationFrameEntry;
          const frameEnd = entry.startTime + entry.duration;
          const scriptDuration =
            frame.scripts?.reduce(
              (sum, script) => sum + (script.duration ?? 0),
              0
            ) ?? 0;
          const forcedStyleDuration =
            frame.scripts?.reduce(
              (sum, script) => sum + (script.forcedStyleAndLayoutDuration ?? 0),
              0
            ) ?? 0;
          this._rawLongAnimationFrames.push({
            atMs: rounded(entry.startTime - this._sessionStartedAt, 3),
            blockingDurationMs: rounded(frame.blockingDuration ?? 0, 3),
            durationMs: rounded(entry.duration, 3),
            renderDurationMs: rounded(
              frame.styleAndLayoutStart
                ? Math.max(0, frameEnd - frame.styleAndLayoutStart)
                : 0,
              3
            ),
            scripts: (frame.scripts ?? []).map(script => ({
              durationMs: rounded(script.duration ?? 0, 3),
              forcedStyleAndLayoutDurationMs: rounded(
                script.forcedStyleAndLayoutDuration ?? 0,
                3
              ),
              invoker: script.invoker ?? null,
              invokerType: script.invokerType ?? null,
              sourceCharPosition: script.sourceCharPosition ?? null,
              sourceFunctionName: script.sourceFunctionName ?? null,
              sourceURL: script.sourceURL
                ? safeResourceUrl(script.sourceURL)
                : null,
            })),
          });
          frame.scripts?.forEach(script => {
            const sourceURL = script.sourceURL
              ? safeResourceUrl(script.sourceURL)
              : null;
            const key = [
              sourceURL ?? 'unknown-source',
              script.sourceFunctionName ?? 'anonymous',
              script.invoker ?? 'unknown-invoker',
            ].join('|');
            const series = this._longAnimationScripts.get(key) ?? {
              count: 0,
              durationMs: 0,
              forcedStyleAndLayoutDurationMs: 0,
              invoker: script.invoker ?? null,
              invokerType: script.invokerType ?? null,
              sourceCharPosition: script.sourceCharPosition ?? null,
              sourceFunctionName: script.sourceFunctionName ?? null,
              sourceURL,
            };
            series.count += 1;
            series.durationMs += script.duration ?? 0;
            series.forcedStyleAndLayoutDurationMs +=
              script.forcedStyleAndLayoutDuration ?? 0;
            this._longAnimationScripts.set(key, series);
          });
          addSample(this._longAnimationDurations, entry.duration);
          addSample(
            this._longAnimationBlockingDurations,
            frame.blockingDuration ?? 0
          );
          addSample(this._longAnimationScriptDurations, scriptDuration);
          addSample(
            this._longAnimationForcedStyleDurations,
            forcedStyleDuration
          );
          addSample(
            this._longAnimationRenderDurations,
            frame.styleAndLayoutStart
              ? Math.max(0, frameEnd - frame.styleAndLayoutStart)
              : 0
          );
        });
      });
      observer.observe({ type: 'long-animation-frame', buffered: false });
      this._performanceObservers.push(observer);
    }
    if (supported.includes('layout-shift')) {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          const shift = entry as PerformanceEntry & {
            hadRecentInput?: boolean;
            value?: number;
          };
          const score = shift.value ?? 0;
          addSample(this._layoutShiftScores, score);
          if (!shift.hadRecentInput)
            addSample(this._unexpectedLayoutShiftScores, score);
        });
      });
      observer.observe({ type: 'layout-shift', buffered: false });
      this._performanceObservers.push(observer);
    }
    if (supported.includes('gc')) {
      const observer = new PerformanceObserver(list => {
        list
          .getEntries()
          .forEach(entry => addSample(this._gcDurations, entry.duration));
      });
      observer.observe({ type: 'gc', buffered: false });
      this._performanceObservers.push(observer);
    }
    if (supported.includes('resource')) {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          const resource = entry as PerformanceResourceTiming;
          const url = safeResourceUrl(resource.name);
          const key = `${resource.initiatorType}|${url}`;
          const series = this._resources.get(key) ?? {
            count: 0,
            durationMs: 0,
            initiatorType: resource.initiatorType,
            transferSize: 0,
            url,
          };
          series.count += 1;
          series.durationMs += resource.duration;
          series.transferSize += resource.transferSize;
          this._resources.set(key, series);
          this._rawResources.push({
            atMs: rounded(resource.startTime - this._sessionStartedAt, 3),
            decodedBodySize: resource.decodedBodySize,
            durationMs: rounded(resource.duration, 3),
            encodedBodySize: resource.encodedBodySize,
            initiatorType: resource.initiatorType,
            transferSize: resource.transferSize,
            url,
          });
        });
      });
      observer.observe({ type: 'resource', buffered: false });
      this._performanceObservers.push(observer);
    }
    this._frameRequest = requestAnimationFrame(this._collectFrame);
  }

  private _stop() {
    if (!this._recording) return;
    const stoppedAt = performance.now();
    this._recording = false;
    delete document.documentElement.dataset
      .streamlainCanvasPerformanceRecording;
    this._elapsedMs = stoppedAt - this._sessionStartedAt;
    this._cancelPanTestTimers();
    this._stopCollectors();

    const frames = summarize(this._frameSamples);
    const cadenceSource = this._recordingCadence ? 'pre-session' : 'session';
    const cadence =
      this._recordingCadence ?? estimateDisplayCadence(this._frameSamples);
    const panDurations = this._frameInteractionBuckets.get('pan') ?? [];
    const panDurationMs = panDurations.reduce(
      (sum, duration) => sum + duration,
      0
    );
    const { invalidReasons, panCoverage, valid } = evaluatePanTestValidity({
      cadenceAvailable: cadence !== null,
      minimumCoverage: PAN_TEST_MIN_COVERAGE,
      movementUpdates: this._panTestMovementUpdates,
      panDurationMs,
      targetDurationMs: PAN_TEST_DURATION_MS,
    });
    if (this._settleEvidence?.reason === 'timeout') {
      invalidReasons.push('pre-test-cadence-did-not-stabilize');
    }
    const deadlineOverrunMs = this._panTestTargetEndAt
      ? Math.max(0, stoppedAt - this._panTestTargetEndAt)
      : 0;
    const topScripts = [...this._longAnimationScripts.values()]
      .sort((left, right) => right.durationMs - left.durationMs)
      .slice(0, 20)
      .map(series => ({
        ...series,
        durationMs: rounded(series.durationMs),
        forcedStyleAndLayoutDurationMs: rounded(
          series.forcedStyleAndLayoutDurationMs
        ),
      }));
    const resources = [...this._resources.values()]
      .sort((left, right) => right.durationMs - left.durationMs)
      .slice(0, 20)
      .map(series => ({
        ...series,
        durationMs: rounded(series.durationMs),
      }));
    const estimatedDroppedFrames = cadence
      ? countEstimatedDroppedFrames(this._frameSamples, cadence.expectedFrameMs)
      : 0;
    const detailedGfx = summarizeDetailedGfx(this._detailedGfxSamples);
    const elementInventory = canvasElementInventory(this._canvasElementSamples);
    const blockRuntime = blockRuntimeInventory(this._blockRuntimeSamples);
    const viewportCoherence = viewportCoherenceInventory(this._rawGfxSamples);
    const userTiming = collectUserTimingEntries(this._sessionStartedAt);
    const userTimingSignals = userTiming.filter(entry =>
      entry.name.startsWith('streamlain:')
    );
    this._reportValid = valid && invalidReasons.length === 0;
    const report = {
      schema: 'streamlain.edgeless-performance-capture.v18',
      capturedAt: new Date().toISOString(),
      startedAt: new Date(this._sessionStartedAtEpochMs).toISOString(),
      durationMs: Math.round(this._elapsedMs),
      test: {
        kind:
          this._panTestMode === 'diagnostic-suite'
            ? 'automated-diagnostic-suite-segment'
            : 'automated-canvas-pan',
        targetDurationMs: PAN_TEST_DURATION_MS,
        armDurationMs: 0,
        settle: this._settleEvidence,
        deadlineOverrunMs: rounded(deadlineOverrunMs),
        panDurationMs: rounded(panDurationMs),
        panCoveragePercent: rounded(panCoverage * 100, 1),
        movement: {
          horizontalAmplitudeCssPx: PAN_TEST_HORIZONTAL_AMPLITUDE_PX,
          verticalAmplitudeCssPx: PAN_TEST_VERTICAL_AMPLITUDE_PX,
          path: 'horizontal-sine_vertical-double-sine',
          viewportUpdates: this._panTestMovementUpdates,
          distanceCssPx: rounded(this._panTestDistanceCssPx),
          restoredInitialViewport: true,
        },
        panDriverCallback: summarize(this._panDriverDurations),
        valid: this._reportValid,
        invalidReasons,
        blockProbe:
          this._panTestMode === 'diagnostic-suite'
            ? {
                attribution:
                  'Paint-containment candidates are tested on the largest DOM-backed note, list, and database, followed by content-visibility attribution of the list and database subtrees. Compare each delta with the suite baseline.',
                presentationVariant: {
                  css: this._activeDiagnosticScenario?.css ?? 'none',
                  id: this._activeDiagnosticScenario?.id ?? 'baseline',
                  label: this._activeDiagnosticScenario?.label ?? 'Baseline',
                  targetBlockId:
                    this._activeDiagnosticScenario?.targetBlockId ?? null,
                  viewportTransformWrites: this._activeDiagnosticScenario
                    ?.suspendTransformWrites
                    ? 'suspended'
                    : 'normal',
                  visualPresentation:
                    this._activeDiagnosticScenario?.visualPresentation ??
                    'retained',
                },
                targets: this._blockProbeTargetInfo,
              }
            : null,
      },
      environment: {
        deviceMemoryGb:
          (navigator as Navigator & { deviceMemory?: number }).deviceMemory ??
          null,
        devicePixelRatio: rounded(window.devicePixelRatio, 2),
        hardwareConcurrency: navigator.hardwareConcurrency,
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        supportedPerformanceEntries: PerformanceObserver.supportedEntryTypes,
      },
      content: {
        start: this._startContent,
        stop: this._captureContent(),
      },
      frames: {
        ...frames,
        estimatedRefreshRateHz: cadence?.estimatedRefreshRateHz ?? null,
        expectedFrameMs: cadence ? rounded(cadence.expectedFrameMs) : null,
        cadenceSource: cadence ? cadenceSource : null,
        estimatedDroppedFrames,
        estimatedDroppedFramePercent:
          this._frameSamples.length > 0
            ? rounded(
                (estimatedDroppedFrames /
                  (this._frameSamples.length + estimatedDroppedFrames)) *
                  100,
                1
              )
            : 0,
        overBudgetFrames: cadence
          ? this._frameSamples.filter(
              duration => duration >= cadence.expectedFrameMs * 1.5
            ).length
          : 0,
        over33Ms: this._frameSamples.filter(duration => duration > 33.34)
          .length,
        slowest: [...this._frameTimeline]
          .sort((left, right) => right.durationMs - left.durationMs)
          .slice(0, 20),
      },
      interactions: {
        counts: this._interactionEvents,
        durations: summarize(this._interactionDurations),
        inputDelay: summarize(this._interactionInputDelays),
        processingDuration: summarize(this._interactionProcessingDurations),
        presentationDelay: summarize(this._interactionPresentationDelays),
      },
      longTasks: summarize(this._longTaskDurations),
      longAnimationFrames: {
        durations: summarize(this._longAnimationDurations),
        blockingDuration: summarize(this._longAnimationBlockingDurations),
        scriptDuration: summarize(this._longAnimationScriptDurations),
        renderDuration: summarize(this._longAnimationRenderDurations),
        forcedStyleAndLayoutDuration: summarize(
          this._longAnimationForcedStyleDurations
        ),
        topScripts,
      },
      layoutShifts: {
        all: summarizeValues(this._layoutShiftScores),
        unexpected: summarizeValues(this._unexpectedLayoutShiftScores),
      },
      garbageCollection: summarize(this._gcDurations),
      resources,
      diagnostics: {
        instrumentationOverhead: {
          gfxCollectorCallbacks: summarize(this._gfxCollectorDurations),
          domMutationObserverCallbacks: summarize(
            this._domMutationObserverDurations
          ),
        },
        rawGfxSamples: {
          captured: this._rawGfxSamples.length,
          total: this._rawGfxSampleCount,
          truncated: this._rawGfxSampleCount > this._rawGfxSamples.length,
        },
        canvasElements: {
          unique: elementInventory.length,
          rendered: elementInventory.filter(element => element.render.count > 0)
            .length,
          totalRenderCalls: elementInventory.reduce(
            (count, element) => count + element.render.count,
            0
          ),
          totalVisibilityChecks: elementInventory.reduce(
            (count, element) => count + element.visibilityChecks,
            0
          ),
          topByRenderTime: elementInventory.slice(0, 30),
        },
        domBlocks: {
          attributionBoundary:
            'Lit/transform JS is measured per block. Browser style/layout/paint remains frame-level until controlled per-block ablation or CDP tracing.',
          visibilitySemantics:
            'rendered means the block owns visible pixels or has visible descendant blocks. ownBoxVisible and visibleDescendantBlockCount keep both cases distinct; descendantVisibleCssPixelsSum can overlap for nested blocks.',
          measured: blockRuntime.length,
          topByMeasuredJsTime: blockRuntime.slice(0, 50),
        },
        detailedGfx: detailedGfx.slice(0, 100),
        domMutations: [...this._domMutations.values()].sort(
          (left, right) => right.count - left.count
        ),
        viewportCoherence,
        userTimingSignals,
        userTiming: userTiming
          .filter(entry => entry.entryType === 'measure')
          .sort((left, right) => right.durationMs - left.durationMs)
          .slice(0, 100),
      },
      frameCorrelations: {
        byInteraction: summarizeFrameBuckets(
          this._frameInteractionBuckets,
          cadence
        ),
        byVisibleElements: summarizeFrameBuckets(
          this._frameVisibleBuckets,
          cadence
        ),
        byZoom: summarizeFrameBuckets(this._frameZoomBuckets, cadence),
      },
      memory: {
        startHeapMb: this._startHeapMb,
        stopHeapMb: this._usedHeapMb(),
      },
      renderPhases: Object.fromEntries(
        [...this._renderSamples.entries()].map(([phase, series]) => [
          phase,
          {
            ...summarize(series.durations),
            meanItems:
              series.durations.length > 0
                ? rounded(series.itemCountTotal / series.durations.length, 1)
                : 0,
            maxItems: series.itemCountMax,
          },
        ])
      ),
    };
    this._reportObject = report;
    this._report = JSON.stringify(report, null, 2);
    this._panTestTargetEndAt = 0;
  }

  private _stopCollectors() {
    cancelAnimationFrame(this._frameRequest);
    this._frameRequest = 0;
    this._recordingAbort?.abort();
    this._recordingAbort = null;
    this._domMutationObserver?.disconnect();
    this._domMutationObserver = null;
    this._performanceObservers.forEach(observer => observer.disconnect());
    this._performanceObservers.length = 0;
    this._unsubscribePerformance?.();
    this._unsubscribePerformance = null;
  }

  private _usedHeapMb() {
    const memory = (
      performance as Performance & {
        memory?: { usedJSHeapSize?: number };
      }
    ).memory;
    return memory?.usedJSHeapSize
      ? rounded(memory.usedJSHeapSize / 1024 / 1024, 1)
      : null;
  }

  private async _copyText(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      this._copied = true;
      this._copyFailed = false;
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.append(textArea);
      textArea.select();
      this._copied = document.execCommand('copy');
      this._copyFailed = !this._copied;
      textArea.remove();
    }
  }

  private _currentCaptureEntries(prefix = '') {
    if (!this._reportObject) return {};
    const reportContent = (
      this._reportObject as {
        content?: { start?: ContentSnapshot; stop?: ContentSnapshot };
      }
    ).content;
    const json = (value: unknown) =>
      strToU8(`${JSON.stringify(value, null, 2)}\n`);
    const path = (value: string) => `${prefix}${value}`;
    return {
      [path('inventory/canvas-elements.json')]: json(
        canvasElementInventory(this._canvasElementSamples)
      ),
      [path('inventory/block-runtime.json')]: json(
        blockRuntimeInventory(this._blockRuntimeSamples)
      ),
      [path('inventory/dom-blocks.json')]: json({
        start: reportContent?.start?.domBlocks ?? [],
        stop: reportContent?.stop?.domBlocks ?? [],
      }),
      [path('inventory/dom-mutations.json')]: json([
        ...this._domMutations.values(),
      ]),
      [path('inventory/dom-surfaces.json')]: json({
        start: reportContent?.start?.domSurfaces ?? [],
        stop: reportContent?.stop?.domSurfaces ?? [],
      }),
      [path('inventory/viewport-coherence.json')]: json(
        viewportCoherenceInventory(this._rawGfxSamples)
      ),
      [path('raw/frames.json')]: json(this._frameTimeline),
      [path('raw/gfx-samples.json')]: json(this._rawGfxSamples),
      [path('raw/long-animation-frames.json')]: json(
        this._rawLongAnimationFrames
      ),
      [path('raw/resources.json')]: json(this._rawResources),
      [path('raw/user-timing.json')]: json(
        collectUserTimingEntries(this._sessionStartedAt)
      ),
      [path('summary.json')]: json(this._reportObject),
    };
  }

  private _captureDiagnosticSuiteSegment() {
    const scenario = this._activeDiagnosticScenario;
    if (!scenario || !this._reportObject) return;
    const prefix = `scenarios/${String(this._diagnosticSuiteScenarioIndex + 1).padStart(2, '0')}-${scenario.id}/`;
    Object.assign(
      this._diagnosticSuiteEntries,
      this._currentCaptureEntries(prefix)
    );
    this._diagnosticSuiteReports.push(this._reportObject);
  }

  private async _storeBundleAndCopyPath(bundle: Uint8Array, fileName: string) {
    try {
      const response = await fetch('/__streamlain_dev/performance-capture', {
        body: new Blob([bundle], { type: 'application/zip' }),
        headers: {
          'Content-Type': 'application/zip',
          'X-StreamLain-Capture-Name': fileName,
        },
        method: 'POST',
      });
      if (!response.ok) throw new Error(`capture-store-${response.status}`);
      const payload = (await response.json()) as { path?: string };
      if (!payload.path) throw new Error('capture-store-path-missing');
      this._storedCapturePath = payload.path;
      await this._copyText(payload.path);
    } catch {
      this._copyFailed = true;
      this._copied = false;
    }
  }

  private async _saveBundleAndCopyPath() {
    if (!this._reportObject) return;
    const capturedAt = new Date().toISOString();
    const fileName = `canvas-pan-${capturedAt.replace(/[:.]/g, '-')}.zip`;
    const bundle = zipSync(
      {
        'README.txt': strToU8(
          [
            `${BRAND_DISPLAY_NAME} Edgeless Performance Capture v17`,
            '',
            'summary.json: verdichtete Metriken und Top-Owner',
            'raw/frames.json: jeder gemessene Frame',
            'summary.json > test.settle: belegtes rAF-Auspendeln vor der Messung',
            'summary.json > test.blockProbe: eine stabile DOM-GFX-Präsentationsvariante über den vollständigen 3-s-Lauf',
            'raw/gfx-samples.json: alle instrumentierten GFX-Owner-Spans',
            'raw/long-animation-frames.json: Browser-Script/Layout-Attribution',
            'raw/resources.json: Loads während der Messung',
            'raw/user-timing.json: User-Timing-Measures im Testfenster',
            'inventory/canvas-elements.json: jedes geprüfte/gerenderte Canvas-Element',
            'inventory/dom-blocks.json: jeder DOM-Block mit Parent, eigener Box, sichtbarem Subtree und relevanten Styles',
            'inventory/block-runtime.json: Lit-/Transform-JS und Descendant-Mutationen pro Block-ID',
            'inventory/dom-mutations.json: DOM-Churn nach Zieltyp',
            'inventory/dom-surfaces.json: DOM-Canvas-Flächen, Inhalte und Paint-/Compositor-relevante Styles',
            'inventory/viewport-coherence.json: Canvas/DOM-Transformzustände und Skew je Element',
            'cdp/trace.json.gz: vollständiger Chrome-Trace des 3-s-Pans',
            'cdp/cpu-profile.json: Main-Thread-CPU-Profil',
            'cdp/dom-before.json und dom-after.json: Browser-DOM-Snapshots',
            'cdp/node-map.json: BackendNodeId zu Block-ID, Flavour, Tag und Klasse',
            'cdp/invalidations.json: Style-/Layout-/Paint-Invalidierungen mit Node-Zuordnung',
            'cdp/pipeline-events.json: Layout-/Paint-/Raster-/Compositor-Ereignisse',
            'cdp/layer-events.json: Layer-Baum und Repaint-Ereignisse',
            'summary.json > diagnostics.instrumentationOverhead: Messkosten',
            'summary.json > diagnostics.rawGfxSamples: Rohdaten-Kappung',
            '',
            'Das Canvas-Elementinventar bleibt auch bei gekappten Rohdaten',
            'vollständig aggregiert. Rohdaten werden bei 100000 GFX-Spans',
            'begrenzt. Auch mit CDP-Rohtrace bleibt die lokale Dev-Grenze',
            '32 MiB; groessere Laeufe scheitern laut statt still zu kuerzen.',
            '',
            'Der Lauf ist fail-closed: Ohne verbundenen Chrome-CDP-Begleiter',
            'wird kein vermeintlich vollständiges Diagnose-Bundle gespeichert.',
          ].join('\n')
        ),
        ...this._cdpCaptureEntries,
        ...this._currentCaptureEntries(),
      },
      { level: 6 }
    );
    await this._storeBundleAndCopyPath(bundle, fileName);
  }

  private async _saveDiagnosticSuiteAndCopyPath() {
    const capturedAt = new Date().toISOString();
    const fileName = `canvas-pan-${capturedAt.replace(/[:.]/g, '-')}.zip`;
    const json = (value: unknown) =>
      strToU8(`${JSON.stringify(value, null, 2)}\n`);
    const baselineFrames = this._diagnosticSuiteReports[0]?.frames as
      | Record<string, unknown>
      | undefined;
    const baselineMeanMs = Number(baselineFrames?.meanMs ?? 0);
    const scenarios = this._diagnosticSuiteReports.map((report, index) => {
      const frames = report.frames as Record<string, unknown> | undefined;
      const test = report.test as Record<string, unknown> | undefined;
      const renderPhases = report.renderPhases as
        | Record<string, Record<string, unknown>>
        | undefined;
      const scenario = this._diagnosticScenarios[index];
      const frameMeanMs = Number(frames?.meanMs ?? 0);
      return {
        id: scenario?.id ?? `scenario-${index + 1}`,
        label: scenario?.label ?? `Scenario ${index + 1}`,
        targetBlockId: scenario?.targetBlockId ?? null,
        targetBlockIds: scenario?.targetBlockIds ?? [],
        descendantSelector: scenario?.descendantSelector ?? null,
        forceLayoutAfterViewportTask:
          scenario?.forceLayoutAfterViewportTask ?? false,
        suspendRemoteSelectionViewportWrites:
          scenario?.suspendRemoteSelectionViewportWrites ?? false,
        suspendViewportBackgroundWrites:
          scenario?.suspendViewportBackgroundWrites ?? false,
        suspendTransformWrites: scenario?.suspendTransformWrites ?? false,
        visualPresentation: scenario?.visualPresentation ?? 'retained',
        valid: test?.valid ?? false,
        settle: test?.settle ?? null,
        frameCount: frames?.count ?? 0,
        frameMeanMs,
        frameMeanDeltaFromBaselineMs: rounded(frameMeanMs - baselineMeanMs),
        estimatedFps: frameMeanMs > 0 ? rounded(1000 / frameMeanMs, 1) : 0,
        frameP50Ms: frames?.p50Ms ?? 0,
        frameP95Ms: frames?.p95Ms ?? 0,
        frameP99Ms: frames?.p99Ms ?? 0,
        droppedFramePercent: frames?.estimatedDroppedFramePercent ?? 0,
        canvasRenderMeanMs: renderPhases?.['canvas-render']?.meanMs ?? 0,
        canvasRenderP95Ms: renderPhases?.['canvas-render']?.p95Ms ?? 0,
      };
    });
    const bundle = zipSync(
      {
        'README.txt': strToU8(
          [
            `${BRAND_DISPLAY_NAME} Edgeless Performance Full Presentation Suite v25`,
            '',
            'Ein Klick, eine Baseline plus automatisch gewählte Block-Varianten.',
            'Jedes Szenario wird zuerst',
            '3-15 Sekunden per rAF-Kadenz stabilisiert und danach exakt drei',
            'Sekunden über dieselbe Pan-Bahn gemessen.',
            '',
            'suite-summary.json: direkte Vergleichstabelle',
            'scenarios/*/summary.json: vollständige Segmentauswertung',
            'scenarios/*/raw/: Frames, GFX-Spans, Browser-Einträge, Loads',
            'scenarios/*/inventory/: Canvas-/DOM-/Block-Owner',
            '',
            'Jedes Szenario behaelt alle sichtbaren Canvas-Bloecke und deren',
            'Viewport-Bewegung bei. Verglichen werden nur Paint-Containment,',
            'Compositor-Promotion und deren Kombination; eine abschliessende',
            'Baseline-Wiederholung kontrolliert Drift und Nachzieheffekte.',
            'Keine Transform-Writes oder Block-Subtrees werden deaktiviert.',
            'Nach jedem Segment wird der Diagnosezustand vollständig entfernt.',
          ].join('\n')
        ),
        'suite-summary.json': json({
          schema: 'streamlain.edgeless-performance-full-presentation-suite.v8',
          capturedAt,
          scenarioCount: scenarios.length,
          scenarios,
        }),
        ...this._diagnosticSuiteEntries,
      },
      { level: 6 }
    );
    await this._storeBundleAndCopyPath(bundle, fileName);
  }

  override render() {
    if (!this._visible || !this._isEdgeless()) return nothing;
    const frames = summarize(this._frameSamples);
    const canvas = this._renderSamples.get('canvas-render');
    const longAnimationFrames = summarize(this._longAnimationDurations);
    const panDurationMs = (
      this._frameInteractionBuckets.get('pan') ?? []
    ).reduce((sum, duration) => sum + duration, 0);
    const panCoveragePercent = rounded(
      (panDurationMs / PAN_TEST_DURATION_MS) * 100,
      0
    );
    return html`
      <section class="panel" aria-label="Canvas performance recorder">
        <div class="header">
          <div class="title-copy">
            <span class="title">Canvas Performance</span>
            <span class="title-meta">
              <span
                class=${`status ${this._recording || this._armed ? 'recording' : ''}`}
              >
                <span class="status-dot"></span>
                <span>
                  ${this._armed
                    ? `${this._suiteProgress ? `${this._suiteProgress} · ` : ''}Renderzustand stabilisieren · ${(this._countdownMs / 1000).toFixed(1)} s`
                    : this._recording
                      ? `${this._suiteProgress ? `${this._suiteProgress} · ` : ''}Automatischer Pan · ${Math.max(0, (PAN_TEST_DURATION_MS - this._elapsedMs) / 1000).toFixed(1)} s`
                      : this._report
                        ? this._copyFailed
                          ? 'Bundle gespeichert · Zwischenablage fehlgeschlagen'
                          : this._copied
                            ? 'Bundle gespeichert · Pfad kopiert'
                            : 'Bundle wird gespeichert …'
                        : 'Bereit'}
                </span>
              </span>
              <span class="meta-divider">·</span>
              <span class="sample-state">
                ${this._recording ? `${panCoveragePercent}% Pan` : 'Live'}
              </span>
            </span>
          </div>
          <button
            class="icon"
            title="Schließen"
            @click=${() => this._setVisible(false)}
          >
            ×
          </button>
        </div>
        <div class="metrics">
          <div class="metric fps">
            <span class="metric-copy">
              <span class="label">Aktuelle Bildrate</span>
              <span class="metric-detail">Rollierendes 1-Sekunden-Fenster</span>
            </span>
            <span class="value"
              >${this._currentFps}<span class="unit">FPS</span></span
            >
          </div>
          <div class="metric">
            <span class="label">Frame p95</span>
            <span class="value">${frames.p95Ms} ms</span>
          </div>
          <div class="metric">
            <span class="label">Pan-Abdeckung</span>
            <span class="value">${panCoveragePercent} %</span>
          </div>
          <div class="metric">
            <span class="label">Canvas Paint p95</span>
            <span class="value"
              >${canvas ? summarize(canvas.durations).p95Ms : 0} ms</span
            >
          </div>
          <div class="metric">
            <span class="label">Long Frame max</span>
            <span class="value">${longAnimationFrames.maxMs} ms</span>
          </div>
        </div>
        <label class="cdp-option">
          <input
            type="checkbox"
            .checked=${this._cdpCaptureEnabled}
            ?disabled=${this._armed || this._recording}
            @change=${this._onCdpCaptureEnabledChange}
          />
          <span>Chrome-CDP mitschneiden</span>
        </label>
        <div class="actions">
          <button
            class="primary"
            ?disabled=${this._armed || this._recording}
            @click=${this._runBenchmark}
          >
            ${this._armed
              ? 'Bereit machen …'
              : this._recording
                ? '3 s pannen …'
                : this._report
                  ? '3-s-Pan-Test wiederholen'
                  : '3-s-Pan-Test'}
          </button>
          <button
            ?disabled=${this._armed || this._recording}
            @click=${this._runDiagnosticSuite}
          >
            Datenbank-Layer live vergleichen
          </button>
        </div>
        <div class="hint">
          <kbd>⇧ ⌥ F</kbd>
          <span
            >Ein Test ·
            ${this._cdpCaptureEnabled ? 'mit Chrome-CDP' : 'ohne Chrome-CDP'} ·
            ein ZIP-Pfad automatisch kopiert</span
          >
        </div>
        ${this._cdpCaptureError
          ? html`<div class="capture-error">${this._cdpCaptureError}</div>`
          : nothing}
        ${this._storedCapturePath
          ? html`<div class="capture-path">
              <input
                aria-label="Gespeicherter Capture-Pfad"
                readonly
                .value=${this._storedCapturePath}
                @focus=${(event: FocusEvent) =>
                  (event.currentTarget as HTMLInputElement).select()}
              />
              <button @click=${() => this._copyText(this._storedCapturePath)}>
                Pfad kopieren
              </button>
            </div>`
          : nothing}
      </section>
    `;
  }
}

export const canvasPerformanceOverlayWidget = WidgetViewExtension(
  'affine:page',
  AFFINE_CANVAS_PERFORMANCE_OVERLAY,
  literal`${unsafeStatic(AFFINE_CANVAS_PERFORMANCE_OVERLAY)}`
);

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_CANVAS_PERFORMANCE_OVERLAY]: AffineCanvasPerformanceOverlay;
  }
}
